/*
  Keksshop: Eigenbau-Logik (Modul, wird mit defer geladen, keine Bibliotheken)
  - In den Warenkorb per AJAX, danach öffnet die Warenkorb-Schublade (nie Sprung auf /cart)
  - <keks-lieferdatum>: Lieferzeile mit Datum, Werktage ohne Sa/So, Uhrzeit Europe/Berlin
  - <keks-gratisversand>: Restbetrag bis versandkostenfrei aus dem Warenkorb
  - <keks-zusammen>: Passt dazu, mehrere auf einmal in den Warenkorb
  - <keks-set>: 8er-Set oben auf der Kategorieseite, acht Motive auf einmal in den Warenkorb
  - <keks-zuletzt>: Zuletzt angesehen (localStorage)
  - <keks-einklappbar>: Kategoriebeschreibung am Handy einklappbar
  - <keks-reihe>: Karussell-Pfeile für Kartenreihen
  - <keks-countdown>: Countdown bis zum Bestellschluss (Europe/Berlin, Feiertage wie die Lieferzeile)
  - <keks-welten>: Kategorie-Chips über einer Kartenreihe, Nachladen per Section Rendering
  - <keks-fussgruppe>: Gruppen der Fußzeile, am Handy aufklappbar
  - Leere Suche im Kopfbereich führt auf die Seite mit allen Motiven
  - <keks-wunschtermin>, <keks-text-vorschau>, Pflicht-Häkchen im Warenkorb
*/
let EREIGNISSE = null;
try {
  EREIGNISSE = await import('@shopify/events');
} catch (e) {
  EREIGNISSE = null;
}
const ZEILEN_EREIGNIS = (EREIGNISSE && EREIGNISSE.StandardEvents && EREIGNISSE.StandardEvents.cartLinesUpdate) || 'keks:ohne-shopify-ereignisse';
const SCHUBLADE_MERKER = 'keks-schublade-oeffnen';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
/* Texte der Shopsprache aus layout/theme.liquid (locales/*.json, keks.*). Ohne sie gelten die deutschen Texte. */
const TEXTE = window.keksTexte || {};
const SPRACHE = TEXTE.sprache && TEXTE.sprache !== 'de' ? TEXTE.sprache : 'de-DE';
const zweistellig = (n) => String(n).padStart(2, '0');
const alsIso = (d) => `${d.getFullYear()}-${zweistellig(d.getMonth() + 1)}-${zweistellig(d.getDate())}`;
const alsDeutsch = (d) => `${zweistellig(d.getDate())}.${zweistellig(d.getMonth() + 1)}.${d.getFullYear()}`;
const istWerktag = (d) => d.getDay() !== 0 && d.getDay() !== 6;

function plusWerktage(start, anzahl) {
  const ergebnis = new Date(start);
  ergebnis.setHours(0, 0, 0, 0);
  let gezaehlt = 0;
  while (gezaehlt < anzahl) {
    ergebnis.setDate(ergebnis.getDate() + 1);
    if (istWerktag(ergebnis)) gezaehlt += 1;
  }
  return ergebnis;
}

function geld(cent, waehrung) {
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: waehrung || 'EUR' }).format(cent / 100);
  } catch (e) {
    return `${(cent / 100).toFixed(2).replace('.', ',')} €`;
  }
}

/* =====================================================================
   In den Warenkorb, dann Schublade
   ===================================================================== */
function schubladeOeffnen() {
  const schublade = document.querySelector('theme-drawer#cart-drawer');
  if (schublade && typeof schublade.open === 'function' && !schublade.isOpen) schublade.open();
}

function warenkorbBereiche() {
  const ids = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
  return Array.from(new Set(ids));
}

export async function inDenWarenkorb(positionen, quelle) {
  if (!EREIGNISSE || !EREIGNISSE.CartLinesUpdateEvent) return ohneEreignisseHinzufuegen(positionen);
  const aufgeschoben = EREIGNISSE.CartLinesUpdateEvent.createPromise();
  const anzahl = positionen.reduce((summe, position) => summe + position.quantity, 0);
  let erledigt = false;

  (quelle && quelle.isConnected ? quelle : document).dispatchEvent(
    new EREIGNISSE.CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: positionen.map((position) => ({ merchandiseId: String(position.id), quantity: position.quantity })),
      promise: aufgeschoben.promise,
    })
  );

  try {
    const antwort = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: positionen,
        sections: warenkorbBereiche().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const daten = await antwort.json();
    const fehler = !antwort.ok || Boolean(daten.status);
    const warenkorb = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();

    aufgeschoben.resolve({
      cart: EREIGNISSE.CartLinesUpdateEvent.createCartFromAjaxResponse(warenkorb),
      detail: {
        items: warenkorb.items,
        source: 'keks-shop',
        itemCount: anzahl,
        sections: fehler ? undefined : daten.sections,
        didError: fehler,
      },
    });
    erledigt = true;
    document.dispatchEvent(new CustomEvent('keks:warenkorb', { detail: { cart: warenkorb } }));

    if (fehler) throw new Error(daten.description || daten.message || TEXTE.fehler || 'Das hat nicht geklappt.');
    window.setTimeout(schubladeOeffnen, 60);
    return warenkorb;
  } catch (fehler) {
    if (!erledigt) aufgeschoben.reject(fehler);
    throw fehler;
  }
}

/* Rückfall ohne Shopify-Ereignisse: hinzufügen, Seite neu laden, danach Schublade öffnen. Nie Sprung auf /cart. */
async function ohneEreignisseHinzufuegen(positionen) {
  const antwort = await fetch(`${ROOT}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: positionen }),
  });
  const daten = await antwort.json();
  if (!antwort.ok || daten.status) throw new Error(daten.description || daten.message || TEXTE.fehler || 'Das hat nicht geklappt.');
  try {
    sessionStorage.setItem(SCHUBLADE_MERKER, '1');
  } catch (e) {
    /* Speicher gesperrt */
  }
  window.location.reload();
  return null;
}

function merkerSchublade() {
  let gemerkt = null;
  try {
    gemerkt = sessionStorage.getItem(SCHUBLADE_MERKER);
    sessionStorage.removeItem(SCHUBLADE_MERKER);
  } catch (e) {
    gemerkt = null;
  }
  if (gemerkt) window.setTimeout(schubladeOeffnen, 300);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', merkerSchublade);
else merkerSchublade();

function fehlerZeigen(bereich, text) {
  const ziel = bereich && bereich.querySelector('[data-keks-fehler]');
  if (!ziel) return;
  ziel.textContent = text;
  ziel.hidden = false;
  window.setTimeout(() => {
    ziel.hidden = true;
  }, 6000);
}

function hinzugefuegtZeigen(knopf) {
  if (!knopf || !knopf.dataset.textHinzugefuegt) return;
  const beschriftung = knopf.querySelector('[data-knopf-text]') || knopf;
  const vorher = beschriftung.textContent;
  beschriftung.textContent = knopf.dataset.textHinzugefuegt;
  window.setTimeout(() => {
    beschriftung.textContent = vorher;
  }, 2000);
}

document.addEventListener('submit', async (event) => {
  const formular = event.target;
  if (!(formular instanceof HTMLFormElement) || !formular.matches('[data-keks-ajax]')) return;
  event.preventDefault();
  const knopf = formular.querySelector('[type="submit"]');
  const daten = new FormData(formular);
  const id = Number(daten.get('id'));
  const menge = Math.max(1, Number(daten.get('quantity') || 1));
  if (!id) return;
  if (knopf) {
    knopf.disabled = true;
    knopf.setAttribute('aria-busy', 'true');
  }
  try {
    await inDenWarenkorb([{ id, quantity: menge }], formular);
    hinzugefuegtZeigen(knopf);
  } catch (fehler) {
    fehlerZeigen(formular.closest('[data-keks-fehlerbereich]') || formular, fehler.message);
  } finally {
    if (knopf) {
      knopf.disabled = false;
      knopf.removeAttribute('aria-busy');
    }
  }
});

/* =====================================================================
   Leere Suche führt auf die Seite mit allen Motiven
   ===================================================================== */
document.addEventListener('submit', (ereignis) => {
  const formular = ereignis.target;
  if (!(formular instanceof HTMLFormElement) || !formular.matches('[data-keks-suche-leer]')) return;
  const feld = formular.querySelector('input[name="q"]');
  if (feld && String(feld.value || '').trim() !== '') return;
  const ziel = formular.dataset.keksSucheLeer;
  if (!ziel) return;
  ereignis.preventDefault();
  window.location.assign(ziel);
});

/* =====================================================================
   Lieferzeile mit Datum
   ===================================================================== */
function berlinJetzt() {
  const teile = {};
  new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
    .formatToParts(new Date())
    .forEach((teil) => {
      teile[teil.type] = teil.value;
    });
  return {
    tag: new Date(Number(teile.year), Number(teile.month) - 1, Number(teile.day)),
    stunde: Number(teile.hour),
    minute: Number(teile.minute),
  };
}

/* Wort für den Versandtag: „Morgen“, wenn es der nächste Kalendertag ist, sonst der Wochentag. */
function versandtagWort(versandtag, heute) {
  const einTag = 24 * 60 * 60 * 1000;
  const abstand = Math.round((versandtag.getTime() - heute.getTime()) / einTag);
  if (abstand === 1) return TEXTE.morgen || 'Morgen';
  const wort = versandtag.toLocaleDateString(SPRACHE, { weekday: 'long' });
  return wort.charAt(0).toUpperCase() + wort.slice(1);
}

function datumsListe(text) {
  return new Set(String(text || '').match(/\d{4}-\d{2}-\d{2}/g) || []);
}

/*
  Lieferzeile:
  - Zustelltag: Montag bis Freitag und kein Feiertag aus der Einstellung (Samstag und Sonntag keine Zustellung)
  - Versandtag: Zustelltag und nicht in „Tage ohne Versand“ (24.12. und 31.12.)
  - Vor der Uhrzeit (14 Uhr, Europe/Berlin) an einem Versandtag geht es heute raus, sonst am nächsten Versandtag
  - Ankunft: ab dem Versandtag die eingestellten Werktage weiterzählen, nur Zustelltage zählen
  - Mit data-fertigung (Lieferzeile über dem Warenkorb-Knopf): vom Versandtag aus so viele Versandtage
    weiterzählen, das ist der späteste Versandtag für {versand_kurz} und {versand_datum}
    Beispiel bei 3: Do 29.10.2026 vor 14 Uhr -> spätestens Di, 3.11., nach 14 Uhr -> spätestens Mi, 4.11.
  - {morgen_kurz}: immer der nächste Versandtag nach heute, unabhängig von der Uhrzeit (Versandkarte, Marius 29.10.)
    Beispiel: Do 29.10.2026 -> Fr, 30.10., Fr 30.10.2026 -> Mo, 2.11.
  Beispiele: Mi 23.12.2026 vor 14 Uhr -> Versand 23.12. -> Montag, 28. Dezember
             Mi 23.12.2026 nach 14 Uhr -> Versand 28.12. -> Mittwoch, 30. Dezember
             Fr 11.09.2026 nach 14 Uhr -> Versand 14.09. -> Mittwoch, 16. September
*/
class KeksLieferdatum extends HTMLElement {
  connectedCallback() {
    this.aktualisieren();
    /* Die Schublade tauscht beim Aktualisieren den Inhalt aus, ohne das Element neu einzuhängen.
       Dann stünde wieder der Rückfalltext da, deshalb nach jeder Änderung neu rechnen. */
    if (!this.beobachter) this.beobachter = new MutationObserver(() => this.aktualisieren());
    this.beobachter.observe(this, { childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    if (this.beobachter) this.beobachter.disconnect();
  }

  aktualisieren() {
    const uhrzeit = Number(this.dataset.uhrzeit || 14);
    const werktage = Number(this.dataset.werktage || 2);
    const feiertage = datumsListe(this.dataset.feiertage);
    const ohneVersand = datumsListe(this.dataset.nichtVersand);
    const istZustelltag = (datum) => istWerktag(datum) && !feiertage.has(alsIso(datum));
    const istVersandtag = (datum) => istZustelltag(datum) && !ohneVersand.has(alsIso(datum));
    const { tag, stunde } = berlinJetzt();
    const heute = istVersandtag(tag) && stunde < uhrzeit;
    const versandtag = new Date(tag);
    if (!heute) {
      do {
        versandtag.setDate(versandtag.getDate() + 1);
      } while (!istVersandtag(versandtag));
    }
    const fertigung = Number(this.dataset.fertigung || 0);
    const spaetesterVersand = new Date(versandtag);
    for (let schritt = 0; schritt < fertigung; schritt += 1) {
      do {
        spaetesterVersand.setDate(spaetesterVersand.getDate() + 1);
      } while (!istVersandtag(spaetesterVersand));
    }
    const versandKurz = `${spaetesterVersand
      .toLocaleDateString(SPRACHE, { weekday: 'short' })
      .replace('.', '')}, ${spaetesterVersand.getDate()}.${spaetesterVersand.getMonth() + 1}.`;
    const versandDatum = spaetesterVersand.toLocaleDateString(SPRACHE, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
    const morgen = new Date(tag);
    do {
      morgen.setDate(morgen.getDate() + 1);
    } while (!istVersandtag(morgen));
    const morgenKurz = `${morgen
      .toLocaleDateString(SPRACHE, { weekday: 'short' })
      .replace('.', '')}, ${morgen.getDate()}.${morgen.getMonth() + 1}.`;
    // Demo CVR: Zustellspanne ab dem nächsten Versandtag ({morgen_kurz}): 1 Zustelltag bis „Werktage“ Zustelltage
    const kurzDatum = (d) => `${d.toLocaleDateString(SPRACHE, { weekday: 'short' }).replace('.', '')}, ${d.getDate()}.${d.getMonth() + 1}.`;
    const ankunftVon = new Date(morgen);
    do {
      ankunftVon.setDate(ankunftVon.getDate() + 1);
    } while (!istZustelltag(ankunftVon));
    const ankunftBis = new Date(morgen);
    for (let n = 0; n < Math.max(werktage, 1); n += 1) {
      do {
        ankunftBis.setDate(ankunftBis.getDate() + 1);
      } while (!istZustelltag(ankunftBis));
    }
    const ankunft = new Date(versandtag);
    let gezaehlt = 0;
    while (gezaehlt < werktage) {
      ankunft.setDate(ankunft.getDate() + 1);
      if (istZustelltag(ankunft)) gezaehlt += 1;
    }
    const datum = ankunft.toLocaleDateString(SPRACHE, { weekday: 'long', day: 'numeric', month: 'long' });
    const datumKurz = `${zweistellig(ankunft.getDate())}.${zweistellig(ankunft.getMonth() + 1)}.`;
    const wochentag = ankunft.toLocaleDateString(SPRACHE, { weekday: 'short' }).replace('.', '');
    const versandName = heute ? TEXTE.heute || 'Heute' : versandtagWort(versandtag, tag);
    const vorlage = (heute ? this.dataset.textHeute : this.dataset.textSpaeter) || '';
    const ziel = this.querySelector('[data-lieferdatum-text]');
    const text = vorlage
      .split('{uhrzeit}')
      .join(String(uhrzeit))
      .split('{versand_kurz}')
      .join(versandKurz)
      .split('{morgen_kurz}')
      .join(morgenKurz)
      .split('{ankunft_von}')
      .join(kurzDatum(ankunftVon))
      .split('{ankunft_bis}')
      .join(kurzDatum(ankunftBis))
      .split('{versand_datum}')
      .join(versandDatum)
      .split('{datum_kurz}')
      .join(datumKurz)
      .split('{wochentag}')
      .join(wochentag)
      .split('{versandtag}')
      .join(versandName)
      .split('{datum}')
      .join(datum);
    if (ziel && vorlage && ziel.textContent !== text) ziel.textContent = text;
  }
}
if (!customElements.get('keks-lieferdatum')) customElements.define('keks-lieferdatum', KeksLieferdatum);

/* =====================================================================
   Versandkostenfrei-Hinweis
   ===================================================================== */
class KeksGratisversand extends HTMLElement {
  connectedCallback() {
    this.nachAenderung = (event) => {
      if (event && event.detail && event.detail.cart && event.type === 'keks:warenkorb') {
        this.anzeigen(event.detail.cart.total_price, event.detail.cart);
      } else {
        window.setTimeout(() => this.laden(), 900);
      }
    };
    document.addEventListener('keks:warenkorb', this.nachAenderung);
    document.addEventListener(ZEILEN_EREIGNIS, this.nachAenderung);
    if (this.hasAttribute('data-nachladen')) this.laden();
  }

  disconnectedCallback() {
    document.removeEventListener('keks:warenkorb', this.nachAenderung);
    document.removeEventListener(ZEILEN_EREIGNIS, this.nachAenderung);
  }

  async laden() {
    try {
      const warenkorb = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
      this.anzeigen(warenkorb.total_price, warenkorb);
    } catch (e) {
      /* ohne Netz bleibt der Text aus Liquid */
    }
  }

  anzeigen(summe, warenkorb) {
    const schwelle = Number(this.dataset.schwelle || 0);
    if (!schwelle) return;
    const rest = schwelle - summe;
    const zweite = Number(this.dataset.ziel || 0);
    const dritte = Number(this.dataset.ziel2 || 0);
    let text;
    let betrag = '';
    let link = false;
    let erreicht = rest <= 0;
    let fortschritt = summe;
    let ntAn = false;
    const baumAn = zweite > schwelle && summe >= zweite;
    if (zweite > schwelle) {
      /* Geschenk-Aktion: erst bis Versand gratis, dann bis zum Geschenk */
      erreicht = summe >= zweite;
      if (erreicht) text = this.dataset.textErreicht;
      else if (rest <= 0) {
        betrag = geld(zweite - summe, this.dataset.waehrung);
        text = (this.dataset.textZwischen || '').replace('{betrag}', betrag);
      } else {
        betrag = geld(rest, this.dataset.waehrung);
        text = (this.dataset.textOffenZiel || '').replace('{betrag}', betrag);
      }
      if (dritte > zweite && erreicht) {
        /* Name & Text gratis: gerechnet ohne Name-&-Text-Zeilen, damit das Geschenk die Grenze nicht selbst hält */
        const gratisId = Number(this.dataset.ntProdukt || 0);
        const bezahltId = Number(this.dataset.ntBezahlt || 0);
        const zeilen = warenkorb && Array.isArray(warenkorb.items) ? warenkorb.items : null;
        const ware = zeilen
          ? zeilen.filter((z) => z.product_id !== gratisId && z.product_id !== bezahltId).reduce((n, z) => n + z.final_line_price, 0)
          : summe;
        const drin = zeilen ? zeilen.some((z) => z.product_id === gratisId) : false;
        fortschritt = ware;
        if (drin) {
          text = this.dataset.textNtDrin;
          ntAn = true;
        } else if (ware >= dritte) {
          /* Große Zeile „ist jetzt gratis“, der Kasten darunter führt zur Texteingabe */
          text = this.dataset.textNtJetzt || this.dataset.textNtLink;
          link = true;
          ntAn = true;
        } else {
          betrag = geld(dritte - ware, this.dataset.waehrung);
          text = (this.dataset.textZwischen2 || '').replace('{betrag}', betrag);
          erreicht = false;
        }
      }
    } else {
      text = erreicht
        ? this.dataset.textErreicht
        : (this.dataset.textBetrag || '').replace('{betrag}', geld(rest, this.dataset.waehrung));
    }
    const ziel = this.querySelector('[data-gratis-text]');
    const kasten = this.querySelector('[data-nt-kasten]');
    if (kasten) {
      kasten.hidden = !link;
      this.querySelectorAll('[data-stufe]').forEach((punkt) => {
        punkt.classList.toggle('keks-stufe--an', punkt.dataset.stufe === 'baum' ? baumAn : ntAn);
      });
    }
    if (ziel && text && kasten) {
      satzFett(ziel, text, betrag);
    } else if (ziel && text) {
      if (link && this.dataset.ntLink) {
        const a = document.createElement('a');
        a.href = this.dataset.ntLink;
        a.textContent = text;
        ziel.replaceChildren(a);
      } else ziel.textContent = text;
    }
    this.classList.toggle('keks-gratisversand--erreicht', erreicht);
    const leiste = this.querySelector('[data-gratis-leiste]');
    if (leiste) {
      const ende = dritte > zweite && zweite > schwelle ? dritte : zweite > schwelle ? zweite : schwelle;
      const prozent = Math.max(0, Math.min(100, Math.round(((dritte > zweite ? fortschritt : summe) / ende) * 100)));
      leiste.style.inlineSize = `${prozent}%`;
      const rahmen = leiste.parentElement;
      if (rahmen) rahmen.setAttribute('aria-valuenow', String(prozent));
    }
  }
}
if (!customElements.get('keks-gratisversand')) customElements.define('keks-gratisversand', KeksGratisversand);

/* Satz mit fettem Betrag und fetten *Sternchen-Stellen*, ohne HTML aus Texten zu übernehmen */
function satzFett(ziel, text, betrag) {
  const teile = [];
  const fett = (inhalt) => {
    const s = document.createElement('strong');
    s.textContent = inhalt;
    return s;
  };
  text.split('*').forEach((stueck, i) => {
    const bits = betrag ? stueck.split(betrag) : [stueck];
    bits.forEach((bit, k) => {
      if (k) teile.push(fett(betrag));
      if (bit) teile.push(i % 2 ? fett(bit) : document.createTextNode(bit));
    });
  });
  ziel.replaceChildren(...teile);
}

/* =====================================================================
   A/B-Test: Variante als verstecktes Merkmal an den Warenkorb, damit sie an der Bestellung steht
   ===================================================================== */
async function testMarkieren() {
  const variante = window.keksTest;
  if (!variante) return;
  try {
    if (window.sessionStorage.getItem('keks-test') === variante) return;
  } catch (e) {
    /* ohne Speicher einfach weiter */
  }
  try {
    const stand = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
    if (!stand.attributes || stand.attributes._test_variante !== variante) {
      await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _test_variante: variante } }),
      });
    }
    try {
      window.sessionStorage.setItem('keks-test', variante);
    } catch (e) {
      /* egal */
    }
  } catch (e) {
    /* ohne Netz kein Merkmal, Bestellung geht trotzdem */
  }
}
testMarkieren();

/* =====================================================================
   Vorweihnachtsaktion: Geschenk ab eigener Grenze (snippets/keks-geschenk-schwelle.liquid)
   Daten aus layout/theme.liquid (window.keksGeschenk), nur solange die Aktion läuft.
   Liegt der Warenkorb ohne Geschenk über der Grenze, kommt das Geschenk (0 €) einmal dazu,
   darunter fällt es wieder raus. Danach werden Schublade und Warenkorbseite neu gezeichnet.
   ===================================================================== */
const GESCHENK = window.keksGeschenk && window.keksGeschenk.variante ? window.keksGeschenk : null;
/* Zweites Geschenk: Name & Text gratis ab eigener Grenze. Nie automatisch hinzufügen (braucht einen Text),
   nur umtauschen: unter der Grenze wird das Geschenk zum bezahlten Ausstecher (gleiche Eigenschaften),
   darüber wird ein bezahlter (1 Stück) zum Geschenk. Gerechnet ohne Name-&-Text-Zeilen. */
const GESCHENK2 = window.keksGeschenk2 && window.keksGeschenk2.variante && window.keksGeschenk2.bezahlt ? window.keksGeschenk2 : null;
let geschenkLaeuft = false;

function geschenkVorbei(ende) {
  if (!ende) return false;
  const jetzt = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());
  return jetzt > ende;
}

async function warenkorbPost(pfad, inhalt) {
  const antwort = await fetch(`${ROOT}${pfad}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(inhalt),
  });
  const daten = await antwort.json();
  if (!antwort.ok || daten.status) throw new Error('warenkorb');
  return daten;
}

async function geschenkPruefen(warenkorb) {
  if ((!GESCHENK && !GESCHENK2) || geschenkLaeuft) return;
  geschenkLaeuft = true;
  try {
    let stand =
      warenkorb && Array.isArray(warenkorb.items)
        ? warenkorb
        : await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
    const vorher = stand.item_count;
    let geaendert = false;

    if (GESCHENK2) {
      const istNt = (zeile) => zeile.product_id === GESCHENK2.produkt || zeile.product_id === GESCHENK2.bezahltProdukt;
      const gratis = stand.items.filter((zeile) => zeile.variant_id === GESCHENK2.variante);
      const bezahlt = stand.items.filter((zeile) => zeile.variant_id === GESCHENK2.bezahlt);
      const ware = stand.items
        .filter((zeile) => !istNt(zeile) && !(GESCHENK && zeile.variant_id === GESCHENK.variante))
        .reduce((summe, zeile) => summe + zeile.final_line_price, 0);
      const soll = !geschenkVorbei(GESCHENK2.ende) && ware >= GESCHENK2.schwelle ? 1 : 0;
      const ist = gratis.reduce((summe, zeile) => summe + zeile.quantity, 0);
      if (ist > soll) {
        let zuviel = ist - soll;
        const updates = {};
        const neu = [];
        for (const zeile of gratis) {
          if (!zuviel) break;
          const weg = Math.min(zeile.quantity, zuviel);
          zuviel -= weg;
          updates[zeile.key] = zeile.quantity - weg;
          neu.push({ id: GESCHENK2.bezahlt, quantity: weg, properties: zeile.properties || {} });
        }
        await warenkorbPost('cart/add.js', { items: neu });
        stand = await warenkorbPost('cart/update.js', { updates });
        geaendert = true;
      } else if (ist < soll && bezahlt.length) {
        const zeile = bezahlt[0];
        await warenkorbPost('cart/add.js', { items: [{ id: GESCHENK2.variante, quantity: 1, properties: zeile.properties || {} }] });
        stand = await warenkorbPost('cart/update.js', { updates: { [zeile.key]: zeile.quantity - 1 } });
        geaendert = true;
      }
    }

    /* Baum erst danach: ein getauschter Name & Text zählt mit seinem neuen Preis */
    if (GESCHENK) {
      const ware = stand.items
        .filter((zeile) => zeile.variant_id !== GESCHENK.variante)
        .reduce((summe, zeile) => summe + zeile.final_line_price, 0);
      const soll = !geschenkVorbei(GESCHENK.ende) && ware >= GESCHENK.schwelle ? 1 : 0;
      const ist = stand.items
        .filter((zeile) => zeile.variant_id === GESCHENK.variante)
        .reduce((summe, zeile) => summe + zeile.quantity, 0);
      if (soll !== ist) {
        stand = await warenkorbPost('cart/update.js', { updates: { [GESCHENK.variante]: soll } });
        geaendert = true;
      }
    }

    if (!geaendert) return;
    const bereiche = warenkorbBereiche();
    const daten = await warenkorbPost('cart/update.js', {
      updates: {},
      sections: bereiche.join(','),
      sections_url: window.location.pathname,
    });

    if (EREIGNISSE && EREIGNISSE.CartLinesUpdateEvent) {
      const aufgeschoben = EREIGNISSE.CartLinesUpdateEvent.createPromise();
      const variante = GESCHENK ? GESCHENK.variante : GESCHENK2.variante;
      document.dispatchEvent(
        new EREIGNISSE.CartLinesUpdateEvent({
          action: daten.item_count >= vorher ? 'add' : 'remove',
          context: 'cart',
          lines: [{ merchandiseId: String(variante), quantity: 1 }],
          promise: aufgeschoben.promise,
        })
      );
      aufgeschoben.resolve({
        cart: EREIGNISSE.CartLinesUpdateEvent.createCartFromAjaxResponse(daten),
        detail: { items: daten.items, source: 'keks-geschenk', itemCount: daten.item_count, sections: daten.sections, didError: false },
      });
    } else if (bereiche.length) {
      window.location.reload();
    }
  } catch (e) {
    /* ohne Netz bleibt der Warenkorb, wie er ist */
  } finally {
    geschenkLaeuft = false;
  }
}

/* =====================================================================
   Aufpreis fürs Anpassen (Konfigurator, assets/keks-anpassen.js): zu jedem angepassten Ausstecher (_Anpassung) gehört
   die Position „Anpassung deines Ausstechers“ (_Aufpreis) mit derselben Menge. Sie hat keine eigenen Knöpfe
   (snippets/cart-products.liquid); hier folgt sie Menge und Löschen des Ausstechers. Paar über _Paar =
   aufpreisPaar(Variante, _Anpassung), das keks-anpassen.js beim Hinzufügen setzt — gleiche Anpassung = eine Zeile, die
   Shopify zusammenlegt. Aufpreis-Positionen ohne Ausstecher (oder ohne _Paar, vor dem 04.11.2026 hinzugefügt) fallen
   raus, fehlt einem Ausstecher der Aufpreis, kommt er dazu (Variante aus einer vorhandenen Aufpreis-Position, sonst
   AUFPREIS_PRODUKT). Der Bestellabruf eskaliert, wenn trotzdem einer fehlt (app.aufpreis_fehlt).
   ===================================================================== */
const AUFPREIS_PRODUKT = 'anpassung-ausstecher';
let aufpreisLaeuft = false;

export function aufpreisPaar(variante, anpassung) {
  /* FNV-1a, 32 Bit: kurz und in keks-anpassen.js und hier gleich */
  let h = 0x811c9dc5;
  for (const zeichen of `${variante}|${anpassung}`) {
    h ^= zeichen.codePointAt(0);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(36);
}

const istAufpreis = (zeile) => Boolean(zeile.properties && zeile.properties._Aufpreis);
const istAngepasst = (zeile) => !istAufpreis(zeile) && Boolean(zeile.properties && zeile.properties._Anpassung);

async function aufpreisVariante(aufpreise) {
  if (aufpreise.length) return aufpreise[0].variant_id;
  try {
    const produkt = await (await fetch(`${ROOT}products/${AUFPREIS_PRODUKT}.js`, { headers: { Accept: 'application/json' } })).json();
    return produkt && produkt.variants && produkt.variants[0] && produkt.variants[0].available ? produkt.variants[0].id : null;
  } catch (e) {
    return null;
  }
}

/* Schublade und Warenkorbseite nach einer eigenen Änderung neu zeichnen (wie bei den Geschenken); gibt den Stand zurück */
async function warenkorbNeuZeichnen(vorher, variante, quelle) {
  const bereiche = warenkorbBereiche();
  const daten = await warenkorbPost('cart/update.js', {
    updates: {},
    sections: bereiche.join(','),
    sections_url: window.location.pathname,
  });
  if (EREIGNISSE && EREIGNISSE.CartLinesUpdateEvent) {
    const aufgeschoben = EREIGNISSE.CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new EREIGNISSE.CartLinesUpdateEvent({
        action: daten.item_count >= vorher ? 'add' : 'remove',
        context: 'cart',
        lines: [{ merchandiseId: String(variante), quantity: 1 }],
        promise: aufgeschoben.promise,
      })
    );
    aufgeschoben.resolve({
      cart: EREIGNISSE.CartLinesUpdateEvent.createCartFromAjaxResponse(daten),
      detail: { items: daten.items, source: quelle, itemCount: daten.item_count, sections: daten.sections, didError: false },
    });
  } else if (bereiche.length) {
    window.location.reload();
  }
  return daten;
}

async function aufpreisPruefen(warenkorb) {
  if (aufpreisLaeuft) return warenkorb;
  aufpreisLaeuft = true;
  let stand = warenkorb;
  try {
    if (!stand || !Array.isArray(stand.items)) {
      stand = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();
    }
    const aufpreise = stand.items.filter(istAufpreis);
    const soll = new Map();
    stand.items.filter(istAngepasst).forEach((zeile) => {
      const paar = aufpreisPaar(zeile.variant_id, zeile.properties._Anpassung);
      const eintrag = soll.get(paar) || { menge: 0, zeile };
      eintrag.menge += zeile.quantity;
      soll.set(paar, eintrag);
    });
    if (!aufpreise.length && !soll.size) return stand;
    const updates = {};
    const offen = new Map(soll);
    aufpreise.forEach((zeile) => {
      const eintrag = offen.get(zeile.properties._Paar);
      if (eintrag) {
        if (zeile.quantity !== eintrag.menge) updates[zeile.key] = eintrag.menge;
        offen.delete(zeile.properties._Paar);
      } else {
        updates[zeile.key] = 0;
      }
    });
    const neu = [];
    if (offen.size) {
      const variante = await aufpreisVariante(aufpreise);
      if (variante) {
        offen.forEach(({ menge, zeile }, paar) => {
          const zu = `${zeile.sku || ''} ${zeile.properties.Text || ''}`.trim();
          neu.push({ id: variante, quantity: menge, properties: { _Zu: zu, _Aufpreis: 'Anpassen', _Paar: paar } });
        });
      }
    }
    if (!neu.length && !Object.keys(updates).length) return stand;
    const vorher = stand.item_count;
    if (Object.keys(updates).length) await warenkorbPost('cart/update.js', { updates });
    if (neu.length) await warenkorbPost('cart/add.js', { items: neu });
    stand = await warenkorbNeuZeichnen(vorher, neu.length ? neu[0].id : aufpreise[0].variant_id, 'keks-aufpreis');
  } catch (e) {
    /* ohne Netz bleibt der Warenkorb, wie er ist (der Bestellabruf merkt einen fehlenden Aufpreis) */
  } finally {
    aufpreisLaeuft = false;
  }
  return stand;
}

/* Nach jeder Änderung am Warenkorb: erst der Aufpreis, dann die Geschenke (sie rechnen mit dem Betrag samt Aufpreis). */
async function warenkorbPruefen(warenkorb) {
  const stand = await aufpreisPruefen(warenkorb);
  if (GESCHENK || GESCHENK2) await geschenkPruefen(stand);
}

document.addEventListener('keks:warenkorb', (event) => warenkorbPruefen(event.detail && event.detail.cart));
document.addEventListener(ZEILEN_EREIGNIS, (event) => {
  if (event.detail && (event.detail.source === 'keks-geschenk' || event.detail.source === 'keks-aufpreis')) return;
  const fertig = event.promise || (event.detail && event.detail.promise);
  if (fertig && typeof fertig.then === 'function') fertig.then(() => warenkorbPruefen()).catch(() => {});
  else window.setTimeout(() => warenkorbPruefen(), 900);
});
/* Beim Laden nur, wenn es etwas zu prüfen gibt: Geschenk-Aktion oder ein angepasster Ausstecher in der Schublade */
if (GESCHENK || GESCHENK2 || document.querySelector('[data-keks-anpassung], [data-keks-aufpreis]')) warenkorbPruefen();

/* =====================================================================
   Angebot: 4 Ausstecher zum Preis von 3 (nur Ankündigung, kein eigener Rabatt)
   ===================================================================== */
/*
  Zählung für „4 Ausstecher zum Preis von 3“ (gleich in snippets/keks-angebot.liquid):
  - n (standard): Ausstecher aus dem Katalog, also Positionen ohne sichtbare Warenkorb-Eigenschaften,
    mit ihrer Menge. Eigene Motive zählen nicht mit, sie können nie das Gratis-Produkt sein.
  - alle: jede Position mit Menge. Nur dafür da, den leeren Warenkorb zu erkennen.
  - Der Satz kommt aus gratisText(n), die Segmente zeigen n modulo GRATIS_JE.
*/
/* „Ausführung“ (Stempel & Ausstecher oder All-in-One) macht einen Katalog-Ausstecher nicht zum eigenen Motiv. */
const NEUTRAL = ['Ausführung'];

function stueckZaehlen(warenkorb) {
  let alle = 0;
  let standard = 0;
  (warenkorb.items || []).forEach((position) => {
    const eigenschaften = position.properties || {};
    if (eigenschaften._Aufpreis) return;          // Aufpreis fürs Anpassen (anpassen.js): kein Ausstecher
    // angepasste Katalog-Ausstecher (_Anpassung, Konfigurator) sind im Rabatt „4 für 3“ wie jeder Katalog-Ausstecher
    const personalisiert = !eigenschaften._Anpassung && Object.keys(eigenschaften).some(
      (schluessel) => !schluessel.startsWith('_') && !NEUTRAL.includes(schluessel)
        && eigenschaften[schluessel] !== '' && eigenschaften[schluessel] != null
    );
    alle += position.quantity;
    if (!personalisiert) standard += position.quantity;
  });
  return { alle, standard };
}

/*
  Der Satz unter der Leiste. Gleiche Rechnung wie snippets/keks-gratis-text.liquid:
    0 -> „Leg vier in den Warenkorb, der vierte ist gratis.“
    1 -> „Noch 3, dann ist einer gratis.“
    4 -> „Einer ist gratis. Noch 4 bis zum zweiten gratis.“
    8 -> „Zwei sind gratis. Noch 4 bis zum dritten gratis.“
  Ab dem sechsten Gratis-Ausstecher heißt die Ordnungszahl „nächsten“.
*/
/* Jeder wievielte Ausstecher gratis ist. Der automatische Rabatt im Admin heißt „4 Ausstecher zum
   Preis von 3“ (kauft 3, bekommt 1 gratis); dieselbe Zahl steht in snippets/keks-gratis-text.liquid,
   keks-angebot.liquid, keks-warenkorb-gratis.liquid, blocks/keks-zusammen.liquid und
   sections/keks-kategorie-set.liquid. */
const GRATIS_JE = 4;
const ZAHLWOERTER = ['Einer', 'Zwei', 'Drei', 'Vier', 'Fünf'];
const ORDNUNGSZAHLEN = ['zweiten', 'dritten', 'vierten', 'fünften'];

function gratisText(n) {
  const gratis = Math.floor(n / GRATIS_JE);
  const fehlend = GRATIS_JE - (n % GRATIS_JE);
  const vorlagen = TEXTE.gratis;
  if (vorlagen && vorlagen.leer) {
    if (n === 0) return vorlagen.leer;
    if (gratis === 0) return String(vorlagen.noch || '').split('{fehlend}').join(String(fehlend));
    const satz = (vorlagen.mehr && vorlagen.mehr[gratis - 1]) || vorlagen.mehrN || '';
    return satz.split('{fehlend}').join(String(fehlend)).split('{gratis}').join(String(gratis));
  }
  if (n === 0) return 'Leg vier in den Warenkorb, der vierte ist gratis.';
  if (gratis === 0) return `Noch ${fehlend}, dann ist einer gratis.`;
  const wort = gratis === 1 ? 'Einer ist' : `${ZAHLWOERTER[gratis - 1] || gratis} sind`;
  const ordnungszahl = ORDNUNGSZAHLEN[gratis - 1] || 'nächsten';
  return `${wort} gratis. Noch ${fehlend} bis zum ${ordnungszahl} gratis.`;
}

class KeksAngebot extends HTMLElement {
  connectedCallback() {
    this.nachAenderung = (event) => {
      if (event && event.type === 'keks:warenkorb' && event.detail && event.detail.cart) this.anzeigen(event.detail.cart);
      else window.setTimeout(() => this.laden(), 900);
    };
    document.addEventListener('keks:warenkorb', this.nachAenderung);
    document.addEventListener(ZEILEN_EREIGNIS, this.nachAenderung);
    if (this.hasAttribute('data-nachladen')) this.laden();
  }

  disconnectedCallback() {
    document.removeEventListener('keks:warenkorb', this.nachAenderung);
    document.removeEventListener(ZEILEN_EREIGNIS, this.nachAenderung);
  }

  async laden() {
    try {
      this.anzeigen(await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json());
    } catch (e) {
      /* Text aus Liquid bleibt */
    }
  }

  anzeigen(warenkorb) {
    const ziel = this.querySelector('[data-angebot-text]');
    if (!ziel) return;
    const { alle, standard } = stueckZaehlen(warenkorb);
    const text = gratisText(standard);
    /* Volle Punkte der laufenden Runde: 1..GRATIS_JE, bei 4, 8, … alle voll (vorher modulo: dann leer). */
    const punkte = standard === 0 ? 0 : ((standard - 1) % GRATIS_JE) + 1;
    /* In der einen Zeile folgt hinter dem Satz der Trennpunkt, der Schlusspunkt fällt dort weg.
       Dieselbe Kürzung steht in snippets/keks-angebot.liquid. */
    const zeilentext = this.classList.contains('keks-angebot--kompakt') ? text.replace(/\.$/, '') : text;
    ziel.textContent = zeilentext;
    const leerzeile = this.querySelector('[data-angebot-leer]');
    if (leerzeile) leerzeile.textContent = text;
    this.querySelectorAll('[data-angebot-punkt]').forEach((punkt, i) => {
      punkt.classList.toggle('keks-angebot__punkt--voll', i < punkte);
    });
    const anzeige = this.querySelector('.keks-angebot__punkte');
    if (anzeige) anzeige.setAttribute('aria-label', text);
    /* Leerer Warenkorb: nur der Satz, keine leeren Balken. */
    this.classList.toggle('keks-angebot--leer', alle === 0 && this.querySelector('.keks-angebot__leer') !== null);
  }
}
if (!customElements.get('keks-angebot')) customElements.define('keks-angebot', KeksAngebot);



/* =====================================================================
   Passt dazu auf der Produktseite
   ===================================================================== */
class KeksZusammen extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', () => this.rechnen());
    const knopf = this.querySelector('[data-zusammen-knopf]');
    if (knopf) knopf.addEventListener('click', () => this.hinzufuegen(knopf));
    if (this.hasAttribute('data-nachladen')) this.nachladen();
    else this.rechnen();
  }

  gewaehlt() {
    return Array.from(this.querySelectorAll('input[type="checkbox"][data-id]')).filter((feld) => feld.checked);
  }

  rechnen() {
    const felder = this.gewaehlt();
    const preise = felder.map((feld) => Number(feld.dataset.preis || 0));
    const summe = preise.reduce((s, preis) => s + preis, 0);
    this.querySelectorAll('[data-gratis-marke]').forEach((marke) => {
      marke.hidden = true;
    });
    this.querySelectorAll('.keks-zusammen__preis--gratis').forEach((preis) => {
      preis.classList.remove('keks-zusammen__preis--gratis');
    });
    /* Jeder vierte ist gratis: bei 4 Motiven einer, bei 8 Motiven zwei. */
    const gratis = this.dataset.angebot === 'an' ? Math.floor(felder.length / GRATIS_JE) : 0;
    const angebot = gratis > 0;
    let bezahlt = summe;
    if (angebot) {
      /* Günstigste zuerst, bei gleichem Preis die weiter unten stehende Zeile. */
      const reihenfolge = preise.map((preis, i) => i).sort((a, b) => preise[a] - preise[b] || b - a);
      reihenfolge.slice(0, gratis).forEach((i) => {
        const zeile = felder[i].closest('.keks-zusammen__zeile');
        if (zeile) {
          const preisfeld = zeile.querySelector('.keks-zusammen__preis');
          if (preisfeld) preisfeld.classList.add('keks-zusammen__preis--gratis');
          const marke = zeile.querySelector('[data-gratis-marke]');
          if (marke) marke.hidden = false;
        }
        bezahlt -= preise[i];
      });
    }
    const gesamt = this.querySelector('[data-gesamt]');
    if (gesamt) gesamt.textContent = geld(bezahlt, this.dataset.waehrung);
    const vorher = this.querySelector('[data-gesamt-vorher]');
    if (vorher) {
      vorher.textContent = angebot ? geld(summe, this.dataset.waehrung) : '';
      vorher.hidden = !angebot;
    }
    const hinweis = this.querySelector('[data-angebot-hinweis]');
    if (hinweis) hinweis.hidden = !angebot;
    // „Versandkostenfrei“ unter der Summe, sobald der zu zahlende Betrag die Schwelle des Landes erreicht.
    const versand = this.querySelector('[data-zusammen-versand]');
    if (versand) {
      const schwelle = Number(versand.dataset.schwelle || 0);
      versand.hidden = !(schwelle > 0 && felder.length > 0 && bezahlt >= schwelle);
    }
    const knopf = this.querySelector('[data-zusammen-knopf]');
    if (!knopf) return;
    const text = knopf.querySelector('[data-knopf-text]') || knopf;
    knopf.disabled = felder.length === 0;
    let beschriftung = knopf.dataset.textEins || '';
    if (felder.length === GRATIS_JE && knopf.dataset.textVoll) beschriftung = knopf.dataset.textVoll;
    else if (felder.length > 1) beschriftung = knopf.dataset.textAlle || '';
    const zahlt = felder.length - (this.dataset.angebot === 'an' ? Math.floor(felder.length / GRATIS_JE) : 0);
    text.textContent = beschriftung
      .replace('{anzahl}', String(felder.length))
      .replace('{zahlt}', String(zahlt));
  }

  async hinzufuegen(knopf) {
    const positionen = this.gewaehlt().map((feld) => ({ id: Number(feld.dataset.id), quantity: 1 }));
    if (!positionen.length) return;
    knopf.disabled = true;
    knopf.setAttribute('aria-busy', 'true');
    try {
      await inDenWarenkorb(positionen, this);
    } catch (fehler) {
      fehlerZeigen(this, fehler.message);
    } finally {
      knopf.removeAttribute('aria-busy');
      this.rechnen();
    }
  }

  async nachladen() {
    const aktuell = Number(this.dataset.produktId);
    const liste = this.querySelector('[data-zusammen-liste]');
    const vorlage = this.querySelector('template[data-zusammen-vorlage]');
    if (!liste || !vorlage) return;
    const wunsch = Number(this.dataset.extra) || 2;
    const produkte = (await empfehlungenHolen(aktuell, wunsch + 4)).filter((p) => p.id !== aktuell);
    produkte.sort((a, b) => Number(Boolean(b.featured_image)) - Number(Boolean(a.featured_image)));
    let hinzu = 0;
    produkte.slice(0, wunsch).forEach((produkt) => {
      const variante = verfuegbareVariante(produkt);
      if (!variante) return;
      const zeile = vorlage.content.firstElementChild.cloneNode(true);
      const feld = zeile.querySelector('input');
      feld.dataset.id = String(variante.id);
      feld.dataset.preis = String(variante.price);
      zeile.querySelector('[data-titel]').textContent = kurztitel(produkt.title);
      zeile.querySelector('[data-preis]').textContent = geld(variante.price, this.dataset.waehrung);
      const link = zeile.querySelector('[data-link]');
      if (link) link.href = produkt.url;
      const bild = zeile.querySelector('img');
      if (bild && produkt.featured_image) bild.src = bildAdresse(produkt.featured_image, 240);
      else if (bild) bildErsetzen(bild, this);
      liste.appendChild(zeile);
      hinzu += 1;
    });
    if (!hinzu) {
      const rueckfall = this.querySelector('template[data-rueckfall]');
      if (rueckfall) {
        const zeilen = Array.from(rueckfall.content.cloneNode(true).children);
        liste.append(...zeilen);
        hinzu = zeilen.length;
      }
    }
    if (hinzu) this.hidden = false;
    this.rechnen();
  }
}

/* =====================================================================
   Empfehlungen: complementary, dann related, nur verfügbare
   ===================================================================== */
/* Kurztitel für „<Motiv> Keksausstecher mit Keksstempel“, mit oder ohne „, ca. … cm, PLA“ dahinter */
const kurztitel = (titel) => {
  const text = String(titel || '').trim();
  const treffer = text.match(/^(.+?) Keksausstecher mit Keksstempel(?:,.*)?$/);
  if (treffer) return treffer[1].trim();
  /* Andere Sprachen: Muster aus keks.titel.muster, z. B. „Emporte-pièce {M} avec tampon“ */
  const teile = String(TEXTE.titelmuster || '').split('{M}');
  if (teile.length === 2 && (teile[0] || teile[1])) {
    const sicher = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const fremd = text.match(new RegExp(`^${sicher(teile[0])}(.+?)${sicher(teile[1])}(?:,.*)?$`));
    if (fremd) return fremd[1].trim();
  }
  return text;
};

function bildErsetzen(bild, bereich) {
  const vorlage = bereich.querySelector('template[data-bild-ersatz]');
  const ersatz = vorlage && vorlage.content.firstElementChild ? vorlage.content.firstElementChild.cloneNode(true) : null;
  if (ersatz) bild.replaceWith(ersatz);
  else bild.remove();
}
const bildAdresse = (adresse, breite) => `${adresse}${String(adresse).includes('?') ? '&' : '?'}width=${breite}`;
/* Stempel & Ausstecher (SKU …-S): das Paar All-in-One → -S merken; getauscht wird beim Hinzufügen nur mit dem Snippet
   keks-ausfuehrung-merken (Testtheme), sonst bleibt es bei der ersten Variante. */
const verfuegbareVariante = (produkt) => {
  const varianten = produkt.variants || [];
  const variante = varianten.find((v) => v.available !== false);
  const stempel = varianten.find((v) => (v.sku || '').endsWith('-S'));
  if (variante && stempel && stempel !== variante) {
    (window.keksAusfPaare = window.keksAusfPaare || {})[String(variante.id)] = String(stempel.id);
  }
  return variante;
};

async function empfehlungenHolen(produktId, anzahl) {
  for (const absicht of ['complementary', 'related']) {
    try {
      const antwort = await fetch(
        `${ROOT}recommendations/products.json?product_id=${produktId}&limit=${anzahl}&intent=${absicht}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!antwort.ok) continue;
      const daten = await antwort.json();
      const liste = (daten.products || []).filter((produkt) => produkt.available && verfuegbareVariante(produkt));
      if (liste.length) return liste;
    } catch (e) {
      /* nächste Quelle */
    }
  }
  return [];
}

/* =====================================================================
   Passt dazu in der Warenkorb-Schublade (Rückfall ohne Metafeld)
   ===================================================================== */
class KeksPasst extends HTMLElement {
  static get observedAttributes() {
    return ['data-produkt-id', 'data-im-warenkorb'];
  }

  connectedCallback() {
    this.laden();
  }

  attributeChangedCallback(name, alt, neu) {
    if (alt !== null && alt !== neu && this.isConnected) this.laden();
  }

  async laden() {
    if (!this.hasAttribute('data-nachladen')) return;
    const rahmen = this.querySelector('.keks-passt');
    const liste = this.querySelector('[data-passt-liste]');
    const vorlage = this.querySelector('template[data-zeile]');
    if (!rahmen || !liste || !vorlage) return;
    const lauf = (this.lauf = (this.lauf || 0) + 1);
    const imWarenkorb = this.dataset.imWarenkorb || '';
    const produkte = (await empfehlungenHolen(Number(this.dataset.produktId), 10)).filter(
      (produkt) => !imWarenkorb.includes(`,${produkt.id},`)
    );
    if (lauf !== this.lauf) return;
    liste.replaceChildren();
    produkte.slice(0, 3).forEach((produkt) => {
      const variante = verfuegbareVariante(produkt);
      if (!variante) return;
      const zeile = vorlage.content.firstElementChild.cloneNode(true);
      zeile.querySelectorAll('[data-link]').forEach((link) => {
        link.href = produkt.url;
      });
      const titel = kurztitel(produkt.title);
      zeile.querySelector('[data-titel]').textContent = titel;
      zeile.querySelector('[data-preis]').textContent = geld(variante.price, this.dataset.waehrung);
      zeile.querySelector('input[name="id"]').value = String(variante.id);
      const knopf = zeile.querySelector('button');
      if (knopf) knopf.setAttribute('aria-label', `${knopf.dataset.knopfLabel || ''}: ${titel}`);
      const bild = zeile.querySelector('img');
      if (bild && produkt.featured_image) bild.src = bildAdresse(produkt.featured_image, 400);
      else if (bild) bildErsetzen(bild, this);
      liste.appendChild(zeile);
    });
    if (!liste.children.length) {
      const rueckfall = this.querySelector('template[data-rueckfall]');
      if (rueckfall) liste.append(...Array.from(rueckfall.content.cloneNode(true).children));
    }
    rahmen.hidden = liste.children.length === 0;
  }
}
if (!customElements.get('keks-passt')) customElements.define('keks-passt', KeksPasst);
if (!customElements.get('keks-zusammen')) customElements.define('keks-zusammen', KeksZusammen);

/* =====================================================================
   8er-Set der Kategorie: acht Motive, Häkchen, alle auf einmal in den Warenkorb
   Gratis ist jedes vierte Motiv, günstigste zuerst. Der Abzug passiert im Warenkorb.
   ===================================================================== */
class KeksSet extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', () => this.rechnen());
    const knopf = this.querySelector('[data-set-knopf]');
    if (knopf) knopf.addEventListener('click', () => this.hinzufuegen(knopf));
    this.rechnen();
  }

  gewaehlt() {
    return Array.from(this.querySelectorAll('input[type="checkbox"][data-id]')).filter((feld) => feld.checked);
  }

  rechnen() {
    const felder = this.gewaehlt();
    this.querySelectorAll('[data-gratis-marke]').forEach((marke) => {
      marke.hidden = true;
    });
    this.querySelectorAll('.keks-set__preis--gratis').forEach((preis) => {
      preis.classList.remove('keks-set__preis--gratis');
    });

    const eintraege = felder
      .map((feld, stelle) => ({ feld, stelle, preis: Number(feld.dataset.preis || 0) }))
      .sort((a, b) => a.preis - b.preis || a.stelle - b.stelle);
    const summe = eintraege.reduce((wert, eintrag) => wert + eintrag.preis, 0);
    const gratisAnzahl = this.dataset.angebot === 'an' ? Math.floor(eintraege.length / GRATIS_JE) : 0;
    let gratisSumme = 0;
    eintraege.slice(0, gratisAnzahl).forEach((eintrag) => {
      gratisSumme += eintrag.preis;
      const punkt = eintrag.feld.closest('.keks-set__punkt');
      if (!punkt) return;
      const preisfeld = punkt.querySelector('.keks-set__preis');
      if (preisfeld) preisfeld.classList.add('keks-set__preis--gratis');
      const marke = punkt.querySelector('[data-gratis-marke]');
      if (marke) marke.hidden = false;
    });

    const gesamt = this.querySelector('[data-gesamt]');
    if (gesamt) gesamt.textContent = geld(summe - gratisSumme, this.dataset.waehrung);
    const vorher = this.querySelector('[data-gesamt-vorher]');
    if (vorher) vorher.textContent = geld(summe, this.dataset.waehrung);
    const statt = this.querySelector('[data-statt]');
    if (statt) statt.hidden = gratisSumme === 0;
    const hinweis = this.querySelector('[data-angebot-hinweis]');
    if (hinweis) hinweis.hidden = gratisSumme === 0;

    const knopf = this.querySelector('[data-set-knopf]');
    if (!knopf) return;
    knopf.disabled = felder.length === 0;
    const beschriftung = knopf.querySelector('[data-knopf-text]') || knopf;
    const vorlage = felder.length === 1 ? knopf.dataset.textEins || '' : knopf.dataset.textAlle || '';
    beschriftung.textContent = vorlage.replace('{anzahl}', String(felder.length));
  }

  async hinzufuegen(knopf) {
    const positionen = this.gewaehlt().map((feld) => ({ id: Number(feld.dataset.id), quantity: 1 }));
    if (!positionen.length) return;
    knopf.disabled = true;
    knopf.setAttribute('aria-busy', 'true');
    try {
      await inDenWarenkorb(positionen, this);
    } catch (fehler) {
      fehlerZeigen(this, fehler.message);
    } finally {
      knopf.removeAttribute('aria-busy');
      this.rechnen();
    }
  }
}
if (!customElements.get('keks-set')) customElements.define('keks-set', KeksSet);

/* =====================================================================
   Kartenreihe nachladen (Mehr aus der Motivwelt ohne Metafeld)
   ===================================================================== */
class KeksNachladen extends HTMLElement {
  async connectedCallback() {
    if (!this.dataset.url || this.dataset.geladen) return;
    this.dataset.geladen = 'true';
    const spur = this.querySelector('[data-reihe-spur]');
    if (!spur) return;
    let karten = [];
    for (const absicht of ['complementary', 'related']) {
      try {
        const html = await (await fetch(`${this.dataset.url}&intent=${absicht}`)).text();
        const dokument = new DOMParser().parseFromString(html, 'text/html');
        karten = Array.from(dokument.querySelectorAll('[data-reco-karten] > li'));
        if (karten.length) break;
      } catch (e) {
        /* nächste Quelle */
      }
    }
    if (karten.length) {
      spur.append(...karten.map((karte) => document.importNode(karte, true)));
    } else {
      const rueckfall = this.querySelector('template[data-rueckfall]');
      if (rueckfall) spur.append(...Array.from(rueckfall.content.cloneNode(true).children));
    }
    if (spur.children.length) this.hidden = false;
  }
}
if (!customElements.get('keks-nachladen')) customElements.define('keks-nachladen', KeksNachladen);

/* =====================================================================
   Kartenreihe erst beim Scrollen laden (keks-spaeter)
   ===================================================================== */
/*
  Die Seite liefert nur einen Platzhalter mit Überschrift. Kommt er dem Sichtbereich nahe,
  holt das Element dieselbe Sektion über das Section Rendering (`data-url`) und ersetzt sich
  durch deren Inhalt. Eigene Elemente darin (keks-reihe, keks-welten, keks-nachladen,
  product-card) starten beim Einfügen von selbst. Scheitert der Abruf, verschwindet der Platzhalter.
*/
class KeksSpaeter extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.url || this.dataset.geladen) return;
    if (!('IntersectionObserver' in window)) {
      this.laden();
      return;
    }
    const waechter = new IntersectionObserver(
      (eintraege) => {
        if (!eintraege.some((eintrag) => eintrag.isIntersecting)) return;
        waechter.disconnect();
        this.laden();
      },
      { rootMargin: '900px 0px' }
    );
    waechter.observe(this);
  }

  async laden() {
    if (this.dataset.geladen) return;
    this.dataset.geladen = 'true';
    try {
      const antwort = await fetch(this.dataset.url, { headers: { Accept: 'text/html' } });
      if (!antwort.ok) throw new Error(String(antwort.status));
      const dokument = new DOMParser().parseFromString(await antwort.text(), 'text/html');
      const sektion = dokument.querySelector('.shopify-section') || dokument.body;
      if (sektion.querySelector('keks-spaeter')) throw new Error('Antwort ist wieder ein Platzhalter');
      const kinder = Array.from(sektion.children).map((kind) => document.importNode(kind, true));
      if (!kinder.length) throw new Error('leer');
      this.replaceWith(...kinder);
    } catch (fehler) {
      this.hidden = true;
    }
  }
}
if (!customElements.get('keks-spaeter')) customElements.define('keks-spaeter', KeksSpaeter);

/* =====================================================================
   Zuletzt angesehen
   ===================================================================== */
const ZULETZT = 'keks-zuletzt-angesehen';

class KeksZuletzt extends HTMLElement {
  connectedCallback() {
    let liste = [];
    try {
      liste = JSON.parse(localStorage.getItem(ZULETZT) || '[]');
    } catch (e) {
      liste = [];
    }
    let aktuell = null;
    try {
      aktuell = this.dataset.aktuell ? JSON.parse(this.dataset.aktuell) : null;
    } catch (e) {
      aktuell = null;
    }
    const andere = liste.filter((eintrag) => !aktuell || eintrag.handle !== aktuell.handle);
    const minimum = Number(this.dataset.minimum || 2);
    const ziel = this.querySelector('[data-zuletzt-liste]');
    if (ziel && andere.length >= minimum) {
      andere.slice(0, Number(this.dataset.anzahl || 8)).forEach((eintrag) => {
        const punkt = document.createElement('li');
        punkt.className = 'keks-zuletzt__punkt';
        const link = document.createElement('a');
        link.className = 'keks-zuletzt__link';
        link.href = eintrag.url;
        const bildflaeche = document.createElement('span');
        bildflaeche.className = 'keks-zuletzt__bild';
        if (eintrag.bild) {
          const bild = document.createElement('img');
          bild.src = eintrag.bild;
          bild.alt = '';
          bild.loading = 'lazy';
          bild.width = 200;
          bild.height = 200;
          bildflaeche.appendChild(bild);
        } else {
          const vorlage = this.querySelector('template[data-bild-ersatz]');
          if (vorlage && vorlage.content.firstElementChild) bildflaeche.appendChild(vorlage.content.firstElementChild.cloneNode(true));
        }
        const titel = document.createElement('span');
        titel.className = 'keks-zuletzt__titel';
        titel.textContent = eintrag.titel;
        const preis = document.createElement('span');
        preis.className = 'keks-zuletzt__preis';
        preis.textContent = eintrag.preis;
        link.append(bildflaeche, titel, preis);
        punkt.appendChild(link);
        ziel.appendChild(punkt);
      });
      this.hidden = false;
    }
    if (aktuell && aktuell.handle) {
      const neu = [aktuell, ...liste.filter((eintrag) => eintrag.handle !== aktuell.handle)].slice(0, 12);
      try {
        localStorage.setItem(ZULETZT, JSON.stringify(neu));
      } catch (e) {
        /* Speicher gesperrt */
      }
    }
  }
}
if (!customElements.get('keks-zuletzt')) customElements.define('keks-zuletzt', KeksZuletzt);

/* =====================================================================
   Einklappbare Kategoriebeschreibung
   ===================================================================== */
class KeksEinklappbar extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('[data-einklappbar-text]');
    this.knopf = this.querySelector('[data-einklappbar-knopf]');
    if (!this.text || !this.knopf) return;
    this.knopf.addEventListener('click', () => {
      const offen = this.classList.toggle('keks-einklappbar--offen');
      this.knopf.setAttribute('aria-expanded', String(offen));
      this.knopf.textContent = offen ? this.dataset.weniger : this.dataset.mehr;
    });
    requestAnimationFrame(() => {
      this.knopf.hidden = this.text.scrollHeight <= this.text.clientHeight + 2;
    });
  }
}
if (!customElements.get('keks-einklappbar')) customElements.define('keks-einklappbar', KeksEinklappbar);

/* =====================================================================
   Kategorie-Zeile im Feed: Karten erst laden, wenn die Zeile in den Blick kommt
   ===================================================================== */
class KeksFeldreihe extends HTMLElement {
  connectedCallback() {
    const spur = this.querySelector('[data-reihe-spur]');
    if (spur) {
      this.querySelectorAll('[data-reihe-schritt]').forEach((knopf) => {
        knopf.addEventListener('click', () => {
          const richtung = Number(knopf.dataset.reiheSchritt);
          spur.scrollBy({ left: richtung * spur.clientWidth * 0.8, behavior: 'smooth' });
        });
      });
    }
    if (!this.dataset.url) return;
    if (!('IntersectionObserver' in window)) {
      this.laden();
      return;
    }
    const waechter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) {
          waechter.disconnect();
          this.laden();
        }
      },
      { rootMargin: '600px 0px' }
    );
    waechter.observe(this);
  }

  async laden() {
    if (this.dataset.geladen) return;
    this.dataset.geladen = 'true';
    const spur = this.querySelector('[data-reihe-spur]');
    if (!spur) return;
    try {
      const antwort = await fetch(this.dataset.url);
      if (!antwort.ok) throw new Error(String(antwort.status));
      const dokument = new DOMParser().parseFromString(await antwort.text(), 'text/html');
      const karten = Array.from(dokument.querySelectorAll('li'));
      if (!karten.length) return;
      spur.append(...karten.map((karte) => document.importNode(karte, true)));
      spur.hidden = false;
    } catch (e) {
      /* Ohne Karten bleibt die Zeile aus, statt leer dazustehen */
    }
  }
}
if (!customElements.get('keks-feldreihe')) customElements.define('keks-feldreihe', KeksFeldreihe);

/* =====================================================================
   Kartenreihe mit Pfeilen
   ===================================================================== */
class KeksReihe extends HTMLElement {
  connectedCallback() {
    const spur = this.querySelector('[data-reihe-spur]');
    if (!spur) return;
    this.querySelectorAll('[data-reihe-schritt]').forEach((knopf) => {
      knopf.addEventListener('click', () => {
        const richtung = Number(knopf.dataset.reiheSchritt);
        spur.scrollBy({ left: richtung * spur.clientWidth * 0.8, behavior: 'smooth' });
      });
    });
  }
}
if (!customElements.get('keks-reihe')) customElements.define('keks-reihe', KeksReihe);

/* =====================================================================
   Wunschtermin
   ===================================================================== */
class KeksWunschtermin extends HTMLElement {
  connectedCallback() {
    this.datumsfeld = this.querySelector('input[type="date"]');
    this.wertfeld = this.querySelector('input[type="hidden"]');
    this.hinweis = this.querySelector('[data-hinweis]');
    if (!this.datumsfeld || !this.wertfeld || !this.hinweis) return;
    this.datumsfeld.min = alsIso(new Date());
    this.aktualisieren = this.aktualisieren.bind(this);
    this.datumsfeld.addEventListener('change', this.aktualisieren);
    this.datumsfeld.addEventListener('input', this.aktualisieren);
    this.aktualisieren();
  }

  disconnectedCallback() {
    if (!this.datumsfeld) return;
    this.datumsfeld.removeEventListener('change', this.aktualisieren);
    this.datumsfeld.removeEventListener('input', this.aktualisieren);
  }

  aktualisieren() {
    const wert = this.datumsfeld.value;
    if (!wert) {
      this.wertfeld.value = '';
      this.hinweis.hidden = true;
      this.hinweis.textContent = '';
      return;
    }
    const gewaehlt = new Date(`${wert}T00:00:00`);
    if (Number.isNaN(gewaehlt.getTime())) return;
    this.wertfeld.value = alsDeutsch(gewaehlt);
    const sicherAb = plusWerktage(new Date(), Number(this.dataset.werktage) || 10);
    const knapp = gewaehlt < sicherAb;
    const vorlage = knapp ? this.dataset.textKnapp : this.dataset.textOk;
    this.hinweis.textContent = (vorlage || '').replace('{datum}', alsDeutsch(sicherAb));
    this.hinweis.classList.toggle('keks-termin__hinweis--knapp', knapp);
    this.hinweis.classList.toggle('keks-termin__hinweis--ok', !knapp);
    this.hinweis.hidden = false;
  }
}
if (!customElements.get('keks-wunschtermin')) customElements.define('keks-wunschtermin', KeksWunschtermin);

/* =====================================================================
   Text-Vorschau
   ===================================================================== */
const FORMEN = ['herz', 'kreis', 'stern'];
const FORM_SKALA = { herz: 0.85, kreis: 1, stern: 0.62 };

function schriftTabelle(roh) {
  const tabelle = {};
  String(roh || '')
    .split(/\r?\n/)
    .forEach((zeile) => {
      const teile = zeile.split('=');
      if (teile.length < 2) return;
      const name = teile.shift().trim().toLowerCase();
      const familie = teile.join('=').trim();
      if (name && familie) tabelle[name] = familie;
    });
  return tabelle;
}

class KeksTextVorschau extends HTMLElement {
  connectedCallback() {
    this.bereich = this.closest('.shopify-section') || document;
    this.beiEingabe = (event) => {
      if (event.target instanceof Element && this.bereich.contains(event.target)) this.aktualisieren();
    };
    document.addEventListener('input', this.beiEingabe);
    document.addEventListener('change', this.beiEingabe);
    this.aktualisieren();
  }

  disconnectedCallback() {
    document.removeEventListener('input', this.beiEingabe);
    document.removeEventListener('change', this.beiEingabe);
  }

  felder(schluessel) {
    const name = `properties[${schluessel}]`;
    return Array.from(this.bereich.querySelectorAll('[name^="properties["]')).filter((feld) => feld.name === name);
  }

  aktuelleForm() {
    const werte = Array.from(this.bereich.querySelectorAll('input[type="radio"]:checked, select'))
      .filter((feld) => !feld.name.startsWith('properties['))
      .map((feld) => String(feld.value || '').toLowerCase());
    for (const wert of werte) {
      const treffer = FORMEN.find((form) => wert.includes(form));
      if (treffer) return treffer;
    }
    return this.dataset.startForm || 'herz';
  }

  aktualisieren() {
    const svgText = this.querySelector('[data-vorschau-text]');
    const pfad = this.querySelector('[data-vorschau-form]');
    if (!svgText || !pfad) return;
    const textfeld = this.felder(this.dataset.textProperty)[0];
    const text = textfeld ? textfeld.value.trim() : '';
    const anzeige = text || this.dataset.platzhalter || '';
    const schriftfelder = this.felder(this.dataset.schriftProperty);
    const gewaehlt = schriftfelder.find((feld) => (feld.type === 'radio' ? feld.checked : true));
    const schriften = schriftTabelle(this.dataset.schriften);
    const familie = gewaehlt ? schriften[String(gewaehlt.value).trim().toLowerCase()] : '';
    const form = this.aktuelleForm();
    const gross = `${form.charAt(0).toUpperCase()}${form.slice(1)}`;
    if (this.dataset[`pfad${gross}`]) pfad.setAttribute('d', this.dataset[`pfad${gross}`]);
    if (this.dataset[`mitte${gross}`]) svgText.setAttribute('y', this.dataset[`mitte${gross}`]);
    const zeichen = Math.max(1, anzeige.length);
    const basis = zeichen <= 4 ? 64 : Math.max(24, 64 - (zeichen - 4) * 5);
    svgText.setAttribute('font-size', String(Math.round(basis * (FORM_SKALA[form] || 1))));
    svgText.style.fontFamily = familie || '';
    svgText.textContent = anzeige;
    svgText.classList.toggle('keks-vorschau__text--leer', !text);
  }
}
if (!customElements.get('keks-text-vorschau')) customElements.define('keks-text-vorschau', KeksTextVorschau);

/* =====================================================================
   Pflicht-Häkchen im Warenkorb
   ===================================================================== */
const SPEICHER = 'keks-pflicht-signatur';

function speicherLesen() {
  try {
    return sessionStorage.getItem(SPEICHER);
  } catch (e) {
    return null;
  }
}

function speicherSchreiben(wert) {
  try {
    if (wert) sessionStorage.setItem(SPEICHER, wert);
    else sessionStorage.removeItem(SPEICHER);
  } catch (e) {
    /* Speicher gesperrt */
  }
}

function pflichtAbgleichen() {
  document.querySelectorAll('[data-keks-pflicht]').forEach((haekchen) => {
    const bereich = haekchen.closest('.cart__ctas');
    if (!bereich) return;
    if (!haekchen.checked && haekchen.dataset.signatur && speicherLesen() === haekchen.dataset.signatur) {
      haekchen.checked = true;
    }
    const frei = haekchen.checked;
    bereich.querySelectorAll('.cart__checkout-button').forEach((knopf) => {
      knopf.disabled = !frei;
    });
    bereich.classList.toggle('keks-gesperrt', !frei);
  });
}

let geplant = false;
function spaeterAbgleichen() {
  if (geplant) return;
  geplant = true;
  requestAnimationFrame(() => {
    geplant = false;
    pflichtAbgleichen();
    uploadKnoepfeAbgleichen();
  });
}

document.addEventListener('change', (event) => {
  const ziel = event.target;
  if (!(ziel instanceof HTMLInputElement) || !ziel.matches('[data-keks-pflicht]')) return;
  speicherSchreiben(ziel.checked ? ziel.dataset.signatur : '');
  pflichtAbgleichen();
});

document.addEventListener(
  'submit',
  (event) => {
    const formular = event.target;
    if (!(formular instanceof HTMLFormElement) || formular.id !== 'cart-form') return;
    const offen = Array.from(document.querySelectorAll('[data-keks-pflicht]')).some(
      (haekchen) => !haekchen.checked && haekchen.offsetParent !== null
    );
    if (offen) {
      event.preventDefault();
      pflichtAbgleichen();
    }
  },
  true
);

const beobachter = new MutationObserver(spaeterAbgleichen);
const starten = () => {
  pflichtAbgleichen();
  beobachter.observe(document.body, { childList: true, subtree: true });
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', starten);
else starten();

/* =====================================================================
   Datei-Upload an den eigenen Upload-Dienst
   Vertrag: POST {Adresse}/upload, multipart mit file, quelle, email, optional name.
   Erfolg: { ok: true, url, name, groesse }. Fehler: { ok: false, fehler } mit HTTP 4xx.
   ===================================================================== */
const UPLOAD_HOECHSTENS = 25 * 1024 * 1024;

function uploadsZuFormular(formular) {
  if (!(formular instanceof HTMLFormElement)) return [];
  return Array.from(document.querySelectorAll('keks-upload')).filter(
    (upload) => formular.contains(upload) || (formular.id && upload.dataset.form === formular.id)
  );
}

function groesseText(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

class KeksUpload extends HTMLElement {
  connectedCallback() {
    if (this.eingerichtet) return;
    this.eingerichtet = true;
    this.laeuft = false;
    this.eingabe = this.querySelector('[data-datei]');
    this.ablage = this.querySelector('[data-ablage]');
    this.link = this.querySelector('[data-link]');
    this.status = this.querySelector('[data-status]');
    this.fehlerfeld = this.querySelector('[data-fehler]');
    if (!this.eingabe || !this.link) return;
    this.eingabe.addEventListener('change', () => {
      const datei = this.eingabe.files && this.eingabe.files[0];
      if (datei) this.hochladen(datei);
    });
    if (this.ablage) {
      ['dragenter', 'dragover'].forEach((art) =>
        this.ablage.addEventListener(art, (event) => {
          event.preventDefault();
          this.ablage.classList.add('keks-upload__feld--ziehen');
        })
      );
      ['dragleave', 'dragend', 'drop'].forEach((art) =>
        this.ablage.addEventListener(art, () => this.ablage.classList.remove('keks-upload__feld--ziehen'))
      );
      this.ablage.addEventListener('drop', (event) => {
        event.preventDefault();
        const datei = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
        if (datei) this.hochladen(datei);
      });
    }
    const entfernen = this.querySelector('[data-entfernen]');
    if (entfernen) entfernen.addEventListener('click', () => this.entfernen());
    uploadKnoepfeAbgleichen();
  }

  get pflicht() {
    return this.hasAttribute('data-pflicht');
  }

  get bereit() {
    if (this.laeuft) return false;
    return !this.pflicht || Boolean(this.link && this.link.value);
  }

  formular() {
    if (this.dataset.form) return document.getElementById(this.dataset.form);
    return this.closest('form');
  }

  feldwert(name) {
    const formular = this.formular();
    const feld = formular ? formular.querySelector(`[name="${name}"]`) : null;
    return feld ? String(feld.value || '').trim() : '';
  }

  hochladen(datei) {
    const erlaubt = String(this.dataset.endungen || '')
      .split(',')
      .map((endung) => endung.trim().toLowerCase())
      .filter(Boolean);
    const endung = (datei.name.split('.').pop() || '').toLowerCase();
    if (erlaubt.length && !erlaubt.includes(endung)) {
      this.fehler((this.dataset.fehlerArt || '').replace('{endungen}', erlaubt.map((e) => e.toUpperCase()).join(', ')));
      return;
    }
    if (datei.size > UPLOAD_HOECHSTENS) {
      this.fehler(this.dataset.fehlerGroesse);
      return;
    }
    if (this.anfrage) this.anfrage.abort();
    this.fehlerAus();
    this.link.value = '';
    this.laeuft = true;
    this.anzeigen(datei.name, 0, '');
    const vorschau = this.querySelector('[data-vorschau]');
    if (vorschau) {
      if (this.vorschauAdresse) URL.revokeObjectURL(this.vorschauAdresse);
      const zeigbar = /^image\/(png|jpe?g|webp|gif|svg\+xml)$/.test(datei.type);
      this.vorschauAdresse = zeigbar ? URL.createObjectURL(datei) : '';
      vorschau.hidden = !zeigbar;
      if (zeigbar) vorschau.src = this.vorschauAdresse;
    }
    uploadKnoepfeAbgleichen();

    const daten = new FormData();
    daten.append('file', datei);
    daten.append('quelle', this.dataset.quelle || '');
    daten.append('email', this.feldwert('contact[email]'));
    const name = this.feldwert('contact[name]');
    if (name) daten.append('name', name);

    const anfrage = new XMLHttpRequest();
    this.anfrage = anfrage;
    anfrage.open('POST', `${String(this.dataset.url || '').replace(/\/+$/, '')}/upload`);
    anfrage.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) this.anzeigen(datei.name, Math.round((event.loaded / event.total) * 100), '');
    });
    anfrage.addEventListener('load', () => {
      if (this.anfrage !== anfrage) return;
      this.anfrage = null;
      this.laeuft = false;
      let antwort = null;
      try {
        antwort = JSON.parse(anfrage.responseText);
      } catch (e) {
        antwort = null;
      }
      if (anfrage.status >= 200 && anfrage.status < 300 && antwort && antwort.ok && antwort.url) {
        this.link.value = antwort.url;
        this.anzeigen(antwort.name || datei.name, 100, groesseText(antwort.groesse || datei.size));
      } else {
        this.fehler((antwort && antwort.fehler) || this.dataset.fehlerNetz);
      }
      uploadKnoepfeAbgleichen();
    });
    anfrage.addEventListener('error', () => {
      if (this.anfrage !== anfrage) return;
      this.anfrage = null;
      this.laeuft = false;
      this.fehler(this.dataset.fehlerNetz);
      uploadKnoepfeAbgleichen();
    });
    anfrage.send(daten);
  }

  anzeigen(dateiname, prozent, groesse) {
    if (!this.status) return;
    this.status.hidden = false;
    if (this.ablage) this.ablage.hidden = true;
    const name = this.querySelector('[data-dateiname]');
    if (name) name.textContent = dateiname;
    const balken = this.querySelector('[data-balken]');
    if (balken) balken.style.inlineSize = `${prozent}%`;
    const rahmen = this.querySelector('[data-balken-rahmen]');
    if (rahmen) {
      rahmen.setAttribute('aria-valuenow', String(prozent));
      rahmen.hidden = prozent >= 100 && !this.laeuft;
    }
    const meldung = this.querySelector('[data-meldung]');
    if (meldung) meldung.textContent = groesse;
  }

  entfernen() {
    if (this.anfrage) this.anfrage.abort();
    this.anfrage = null;
    this.laeuft = false;
    this.link.value = '';
    this.eingabe.value = '';
    if (this.vorschauAdresse) URL.revokeObjectURL(this.vorschauAdresse);
    this.vorschauAdresse = '';
    if (this.status) this.status.hidden = true;
    if (this.ablage) this.ablage.hidden = false;
    this.fehlerAus();
    uploadKnoepfeAbgleichen();
  }

  fehler(text) {
    this.laeuft = false;
    this.link.value = '';
    this.eingabe.value = '';
    if (this.status) this.status.hidden = true;
    if (this.ablage) this.ablage.hidden = false;
    if (this.fehlerfeld) {
      this.fehlerfeld.textContent = text || '';
      this.fehlerfeld.hidden = !text;
    }
  }

  fehlerAus() {
    if (this.fehlerfeld) {
      this.fehlerfeld.hidden = true;
      this.fehlerfeld.textContent = '';
    }
  }

  hinweisZeigen() {
    if (this.laeuft) return;
    if (this.fehlerfeld && this.pflicht && !this.link.value) {
      this.fehlerfeld.textContent = this.dataset.fehlerPflicht || '';
      this.fehlerfeld.hidden = false;
    }
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
if (!customElements.get('keks-upload')) customElements.define('keks-upload', KeksUpload);

function uploadKnoepfeAbgleichen() {
  const uploads = Array.from(document.querySelectorAll('keks-upload'));
  if (!uploads.length) return;
  uploads.forEach((upload) => {
    if (!(upload instanceof KeksUpload)) return;
    const formular = upload.formular();
    if (!formular) return;
    const offen = uploadsZuFormular(formular).some((eintrag) => eintrag instanceof KeksUpload && !eintrag.bereit);
    const knoepfe = Array.from(formular.querySelectorAll('[type="submit"]'));
    if (formular.id) knoepfe.push(...document.querySelectorAll(`[type="submit"][form="${formular.id}"]`));
    if (upload.pflicht) knoepfe.push(...document.querySelectorAll('.sticky-add-to-cart__button'));
    knoepfe.forEach((knopf) => {
      if (offen) {
        knopf.setAttribute('data-keks-upload-sperre', '');
        knopf.disabled = true;
      } else if (knopf.hasAttribute('data-keks-upload-sperre')) {
        knopf.removeAttribute('data-keks-upload-sperre');
        knopf.disabled = false;
      }
    });
  });
}

document.addEventListener(
  'submit',
  (event) => {
    const offen = uploadsZuFormular(event.target).find((upload) => upload instanceof KeksUpload && !upload.bereit);
    if (offen) {
      event.preventDefault();
      event.stopImmediatePropagation();
      offen.hinweisZeigen();
    }
  },
  true
);

document.addEventListener(
  'click',
  (event) => {
    const knopf = event.target instanceof Element ? event.target.closest('.sticky-add-to-cart__button, .add-to-cart-button') : null;
    if (!knopf) return;
    const offen = Array.from(document.querySelectorAll('keks-upload[data-pflicht]')).find(
      (upload) => upload instanceof KeksUpload && !upload.bereit
    );
    if (offen) {
      event.preventDefault();
      event.stopImmediatePropagation();
      offen.hinweisZeigen();
    }
  },
  true
);

/* =====================================================================
   Anfrageformular: Anfrageart und Stückzahl vorauswählen (Knopf oder ?anfrageart=)
   ===================================================================== */
function anfrageVorwaehlen(art, stueckzahl, scrollen) {
  const feld = document.querySelector('select[data-keks-anfrageart-feld]');
  if (!feld) return false;
  if (art && Array.from(feld.options).some((option) => option.value === art)) feld.value = art;
  const mengen = document.querySelector('select[data-keks-stueckzahl-feld]');
  if (mengen && stueckzahl && Array.from(mengen.options).some((option) => option.value === stueckzahl)) mengen.value = stueckzahl;
  if (scrollen) {
    const ziel = document.getElementById('anfrage') || feld;
    ziel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    feld.focus({ preventScroll: true });
  }
  return true;
}

document.addEventListener('click', (event) => {
  const knopf = event.target instanceof Element ? event.target.closest('[data-keks-anfrageart]') : null;
  if (!knopf) return;
  if (anfrageVorwaehlen(knopf.dataset.keksAnfrageart, knopf.dataset.keksStueckzahl, true)) event.preventDefault();
});

(() => {
  const parameter = new URLSearchParams(window.location.search);
  const art = parameter.get('anfrageart');
  if (art) anfrageVorwaehlen(art, parameter.get('stueckzahl'), true);
})();

/* =====================================================================
   Countdown bis zum Bestellschluss
   ===================================================================== */
/*
  Vor 14 Uhr an einem Versandtag: „Bestell in 2 Std 14 Min, dann geht dein Paket heute raus“.
  Danach und am Wochenende: „Bestell bis Montag 14 Uhr, dann geht dein Paket Montag raus“.
  Gerechnet wird in Europe/Berlin mit denselben Feiertagen wie die Lieferzeile.
  Die Zeile bleibt versteckt, bis der erste Text steht, damit ohne JavaScript nichts leer herumsteht.
*/
class KeksCountdown extends HTMLElement {
  connectedCallback() {
    this.aktualisieren();
    this.takt = window.setInterval(() => this.aktualisieren(), 30000);
  }

  disconnectedCallback() {
    if (this.takt) window.clearInterval(this.takt);
  }

  aktualisieren() {
    const ziel = this.querySelector('[data-countdown-text]');
    if (!ziel) return;
    const uhrzeit = Number(this.dataset.uhrzeit || 14);
    const feiertage = datumsListe(this.dataset.feiertage);
    const ohneVersand = datumsListe(this.dataset.nichtVersand);
    const istVersandtag = (datum) =>
      istWerktag(datum) && !feiertage.has(alsIso(datum)) && !ohneVersand.has(alsIso(datum));
    const { tag, stunde, minute } = berlinJetzt();
    let text = '';

    if (istVersandtag(tag) && stunde < uhrzeit) {
      const restMinuten = (uhrzeit - stunde) * 60 - minute;
      const stunden = Math.floor(restMinuten / 60);
      const minuten = restMinuten % 60;
      const vorlage = stunden > 0 ? this.dataset.textHeute : this.dataset.textMinuten;
      text = String(vorlage || '')
        .split('{stunden}')
        .join(String(stunden))
        .split('{minuten}')
        .join(String(minuten));
    } else {
      const naechster = new Date(tag);
      do {
        naechster.setDate(naechster.getDate() + 1);
      } while (!istVersandtag(naechster));
      const wochentag = naechster.toLocaleDateString(SPRACHE, { weekday: 'long' });
      text = String(this.dataset.textSpaeter || '')
        .split('{tag}')
        .join(wochentag)
        .split('{uhrzeit}')
        .join(String(uhrzeit));
    }

    if (!text) return;
    if (ziel.textContent !== text) ziel.textContent = text;
    this.hidden = false;
  }
}
if (!customElements.get('keks-countdown')) customElements.define('keks-countdown', KeksCountdown);

/* =====================================================================
   Kennst du schon: Kategorie-Chips über der Kartenreihe
   ===================================================================== */
/*
  Ein Chip ist ein normaler Link auf seine Kategorie. Mit JavaScript holt der Klick stattdessen
  dieselbe Section im Zusammenhang dieser Kategorie (`/collections/<handle>?section_id=…`)
  und tauscht nur die Spur aus. Geht etwas schief, folgt der Browser dem Link.
*/
class KeksWelten extends HTMLElement {
  connectedCallback() {
    this.spur = this.querySelector('[data-reihe-spur]');
    this.chips = this.querySelector('[data-welten-chips]');
    if (!this.spur || !this.chips) return;
    this.chips.addEventListener('click', (ereignis) => {
      const chip = ereignis.target instanceof Element ? ereignis.target.closest('[data-welt]') : null;
      if (!chip || ereignis.metaKey || ereignis.ctrlKey || ereignis.shiftKey) return;
      ereignis.preventDefault();
      this.wechseln(chip);
    });
  }

  async wechseln(chip) {
    if (chip.getAttribute('aria-current') === 'true' || this.hasAttribute('aria-busy')) return;
    const adresse = chip.getAttribute('href');
    this.setAttribute('aria-busy', 'true');
    try {
      const html = await (await fetch(`${adresse}?section_id=${this.dataset.sectionId}`)).text();
      const dokument = new DOMParser().parseFromString(html, 'text/html');
      const neu = dokument.querySelector('[data-reihe-spur]');
      if (!neu || !neu.children.length) throw new Error('leer');
      this.spur.replaceChildren(...Array.from(neu.children).map((karte) => document.importNode(karte, true)));
      this.spur.scrollTo({ left: 0 });
      this.chips.querySelectorAll('[data-welt]').forEach((link) => link.removeAttribute('aria-current'));
      chip.setAttribute('aria-current', 'true');
    } catch (fehler) {
      window.location.href = adresse;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}
if (!customElements.get('keks-welten')) customElements.define('keks-welten', KeksWelten);

/* =====================================================================
   Fußzeile: Gruppen am Handy aufklappbar
   ===================================================================== */
/*
  Ohne JavaScript stehen alle Gruppen offen, es fehlt also nichts.
  Am Handy klappt dieses Element sie beim Laden zu, am Computer bleiben sie offen
  und der Kopf ist kein Knopf mehr (siehe keks-shop.css).
*/
class KeksFussgruppe extends HTMLElement {
  connectedCallback() {
    this.knopf = this.querySelector('[data-fuss-knopf]');
    this.inhalt = this.querySelector('[data-fuss-inhalt]');
    if (!this.knopf || !this.inhalt) return;
    this.schmal = window.matchMedia('(max-width: 749px)');
    this.anpassen = () => {
      if (this.schmal.matches) this.setzen(false);
      else this.setzen(true);
    };
    this.knopf.addEventListener('click', () => {
      if (!this.schmal.matches) return;
      this.setzen(this.knopf.getAttribute('aria-expanded') !== 'true');
    });
    this.schmal.addEventListener('change', this.anpassen);
    this.anpassen();
  }

  disconnectedCallback() {
    if (this.schmal && this.anpassen) this.schmal.removeEventListener('change', this.anpassen);
  }

  setzen(offen) {
    this.knopf.setAttribute('aria-expanded', String(offen));
    this.inhalt.hidden = !offen;
  }
}
if (!customElements.get('keks-fussgruppe')) customElements.define('keks-fussgruppe', KeksFussgruppe);

/* Länderauswahl in der Fußzeile: Auswahl wechseln lädt die Seite mit den Werten des Landes neu. */
document.addEventListener('change', (ereignis) => {
  const feld = ereignis.target;
  if (!feld || !feld.matches('[data-keks-land]')) return;
  const formular = feld.closest('form');
  if (formular) formular.submit();
});

/* =====================================================================
   Kartenreihe mit Motivwelt-Chips (Bestseller)
   Die Karten stehen schon alle in der Reihe, der Chip blendet nur aus.
   ===================================================================== */
class KeksReiheWelten extends HTMLElement {
  connectedCallback() {
    const spur = this.querySelector('[data-reihe-spur]');
    const chips = this.querySelectorAll('[data-welt-chip]');
    if (!spur || chips.length === 0) return;
    chips.forEach((chip) => {
      chip.addEventListener('click', (ereignis) => {
        ereignis.preventDefault();
        const welt = chip.dataset.weltChip || '';
        chips.forEach((anderer) => {
          const aktiv = anderer === chip;
          anderer.classList.toggle('keks-chip--aktiv', aktiv);
          if (aktiv) {
            anderer.setAttribute('aria-current', 'true');
          } else {
            anderer.removeAttribute('aria-current');
          }
        });
        spur.querySelectorAll('[data-welt]').forEach((punkt) => {
          punkt.hidden = welt !== '' && punkt.dataset.welt !== welt;
        });
        spur.scrollTo({ left: 0, behavior: 'smooth' });
      });
    });
  }
}
if (!customElements.get('keks-reihe-welten')) customElements.define('keks-reihe-welten', KeksReiheWelten);

/* =====================================================================
   Stückzahl als Variante (eigenes Motiv)
   Der Stepper klickt den passenden Varianten-Knopf, damit Horizon Preis und
   Warenkorb wie gewohnt aktualisiert. Die Chips selbst sind ausgeblendet.
   ===================================================================== */
class KeksStueckzahl extends HTMLElement {
  connectedCallback() {
    this.wert = this.querySelector('[data-wert]');
    this.anzahl = Number(this.dataset.anzahl || 0);
    this.gewaehlt = Number(this.dataset.gewaehlt || 1);
    if (!this.wert || this.anzahl < 2) return;
    this.querySelectorAll('[data-schritt]').forEach((knopf) => {
      knopf.addEventListener('click', () => this.schritt(Number(knopf.dataset.schritt)));
    });
    // Eintippen: nur Ziffern, übernommen wird beim Verlassen oder mit Enter
    this.wert.addEventListener('input', () => {
      this.wert.value = this.wert.value.replace(/\D/g, '');
    });
    this.wert.addEventListener('change', () => this.setzen(Number(this.wert.value)));
    this.wert.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.setzen(Number(this.wert.value));
    });
    this.wert.addEventListener('focus', () => this.wert.select());
    // Schnellwahl-Kacheln (Demo CVR): eine Kachel setzt die Stückzahl direkt
    this.querySelectorAll('[data-menge]').forEach((kachel) => {
      kachel.addEventListener('click', () => this.setzen(Number(kachel.dataset.menge)));
    });
    this.zeichnen();
  }

  radios() {
    const bereich = this.closest('.product-details, product-details, .shopify-section') || document;
    return Array.from(bereich.querySelectorAll('variant-picker input[type="radio"]'));
  }

  schritt(richtung) {
    this.setzen(this.gewaehlt + richtung);
  }

  setzen(wunsch) {
    const neu = Math.min(Math.max(Math.round(wunsch) || 1, 1), this.anzahl);
    const alt = this.gewaehlt;
    this.gewaehlt = neu;
    this.zeichnen();
    if (neu === alt) return;
    const felder = this.radios();
    const ziel = felder[neu - 1];
    if (ziel && !ziel.checked) ziel.click();
  }

  zeichnen() {
    this.wert.value = String(this.gewaehlt);
    this.querySelectorAll('[data-menge]').forEach((kachel) => {
      kachel.setAttribute('aria-pressed', String(Number(kachel.dataset.menge) === this.gewaehlt));
    });
    this.querySelectorAll('[data-schritt]').forEach((knopf) => {
      const richtung = Number(knopf.dataset.schritt);
      const grenze = richtung < 0 ? this.gewaehlt <= 1 : this.gewaehlt >= this.anzahl;
      knopf.disabled = grenze;
    });
  }
}
if (!customElements.get('keks-stueckzahl')) customElements.define('keks-stueckzahl', KeksStueckzahl);

/* Preisblock folgt der gewaehlten Variante: Horizon holt beim Variantenwechsel die
   Section neu, daraus wird der Preis uebernommen. Die Zeile unter dem Stepper bleibt,
   sie rechnet sich aus der ersten Variante und aendert sich nicht. */
document.addEventListener('shopify:product:select', (ereignis) => {
  const ausloeser = ereignis.target;
  if (!(ausloeser instanceof Element) || ausloeser.closest('product-card')) return;
  const bereich = ausloeser.closest('.shopify-section, dialog');
  const block = bereich && bereich.querySelector('.keks-preisblock');
  if (!block || !ereignis.promise) return;
  ereignis.promise
    .then((ergebnis) => {
      const daten = ergebnis && ergebnis.detail;
      if (!daten || !daten.html) return;
      const neu = daten.html.querySelector('.keks-preisblock .keks-preis');
      const alt = block.querySelector('.keks-preis');
      if (neu && alt) alt.replaceWith(neu.cloneNode(true));
    })
    .catch(() => {});
});

/* =====================================================================
   Kopf: weicher Schatten, sobald die Seite gescrollt ist
   ===================================================================== */
const keksKopfGruppe = document.getElementById('header-group');
if (keksKopfGruppe) {
  const keksKopfSchatten = () => {
    keksKopfGruppe.classList.toggle('keks-kopf--gescrollt', window.scrollY > 8);
  };
  keksKopfSchatten();
  window.addEventListener('scroll', keksKopfSchatten, { passive: true });
}

/* =====================================================================
   Runde 24: Vorschläge im leeren Suchfeld
   Chip füllt das Feld und sucht sofort. Escape und ein Klick außerhalb
   schließen die Klappliste am Computer.
   ===================================================================== */
document.addEventListener('click', (ereignis) => {
  const chip = ereignis.target instanceof Element ? ereignis.target.closest('[data-keks-vorschlag]') : null;
  if (!chip) return;
  const bereich = chip.closest('predictive-search-component');
  const feld = bereich && bereich.querySelector('input.search-input');
  if (!feld) return;
  feld.value = chip.dataset.keksVorschlag || '';
  feld.focus();
  feld.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: feld.value }));
});

document.addEventListener('keydown', (ereignis) => {
  if (ereignis.key !== 'Escape') return;
  const feld = ereignis.target;
  if (!(feld instanceof HTMLInputElement)) return;
  const kopfsuche = feld.closest('.keks-kopfsuche');
  if (!kopfsuche) return;
  kopfsuche.classList.add('keks-kopfsuche--zu');
  feld.blur();
});

document.addEventListener('pointerdown', (ereignis) => {
  document.querySelectorAll('.keks-kopfsuche').forEach((kopfsuche) => {
    if (kopfsuche.contains(ereignis.target)) return;
    kopfsuche.classList.add('keks-kopfsuche--zu');
  });
});

document.addEventListener('focusin', (ereignis) => {
  document.querySelectorAll('.keks-kopfsuche--zu').forEach((kopfsuche) => {
    if (!kopfsuche.contains(ereignis.target)) return;
    kopfsuche.classList.remove('keks-kopfsuche--zu');
  });
});

/* =====================================================================
   Runde 24: Foto direkt auf der Produktseite
   Die Datei bleibt im Formular und geht als Warenkorb-Eigenschaft mit.
   Ohne Foto bleibt der Kaufknopf anklickbar und zeigt den Hinweis.
   Runde 25 (21.10.2026): Shopify nimmt am Warenkorb hoechstens 20 MB
   (sonst 413, der Knopf tat dann still nichts) — groessere Bilder rechnet
   der Browser vorher kleiner. Klappt das Hochladen gar nicht (In-App-
   Browser einer Mail-App), geht „Foto per Mail nachreichen“: bestellt
   ohne Datei, Eigenschaft „Foto: kommt per Mail“, der Zeichner fragt nach
   und das Bild aus der Mail wird das Foto (automatik.fotos_aus_mails).
   ===================================================================== */
const FOTO_HOECHSTENS = 19 * 1024 * 1024;
const FOTO_KANTE = 4000;

async function fotoVerkleinern(datei) {
  try {
    const bild = await createImageBitmap(datei);
    const faktor = Math.min(1, FOTO_KANTE / Math.max(bild.width, bild.height));
    const leinwand = document.createElement('canvas');
    leinwand.width = Math.round(bild.width * faktor);
    leinwand.height = Math.round(bild.height * faktor);
    leinwand.getContext('2d').drawImage(bild, 0, 0, leinwand.width, leinwand.height);
    const blob = await new Promise((fertig) => leinwand.toBlob(fertig, 'image/jpeg', 0.9));
    if (!blob || blob.size > FOTO_HOECHSTENS) return null;
    return new File([blob], `${datei.name.replace(/\.[^.]*$/, '')}.jpg`, { type: 'image/jpeg' });
  } catch {
    // ponytail: HEIC ausserhalb von Safari, PDF, AI lassen sich hier nicht umrechnen — dann bleibt nur der Hinweis
    return null;
  }
}

class KeksFotofeld extends HTMLElement {
  connectedCallback() {
    this.datei = this.querySelector('[data-datei]');
    this.gewaehlt = this.querySelector('[data-gewaehlt]');
    this.vorschau = this.querySelector('[data-vorschau]');
    this.dateiname = this.querySelector('[data-dateiname]');
    this.fehlerfeld = this.querySelector('[data-fehlerfeld]');
    this.spaeterWert = this.querySelector('[data-spaeter-wert]');
    this.spaeterZeile = this.querySelector('[data-spaeter-zeile]');
    this.spaeterAn = this.querySelector('[data-spaeter-an]');
    this.pflicht = this.hasAttribute('data-pflicht');
    this.vorschauUrl = null;
    this.spaeter = false;
    this.rechnet = false;
    if (!this.datei) return;
    this.datei.addEventListener('change', () => this.pruefen());
    const wechseln = this.querySelector('[data-wechseln]');
    if (wechseln) wechseln.addEventListener('click', () => this.datei.click());
    const spaeter = this.querySelector('[data-spaeter]');
    if (spaeter) spaeter.addEventListener('click', () => this.spaeterSetzen(true));
    const doch = this.querySelector('[data-doch]');
    if (doch)
      doch.addEventListener('click', () => {
        this.spaeterSetzen(false);
        this.datei.click();
      });
    this.uebernehmen();
  }

  get formular() {
    return this.datei ? this.datei.form : null;
  }

  get bereit() {
    if (this.rechnet) return false;
    if (!this.pflicht || this.spaeter) return true;
    return Boolean(this.datei && this.datei.files && this.datei.files.length > 0);
  }

  async pruefen() {
    const datei = this.datei.files && this.datei.files[0];
    if (!datei || datei.size <= FOTO_HOECHSTENS) {
      this.uebernehmen();
      return;
    }
    this.rechnet = true;
    const kleiner = await fotoVerkleinern(datei);
    this.rechnet = false;
    if (kleiner) {
      try {
        const liste = new DataTransfer();
        liste.items.add(kleiner);
        this.datei.files = liste.files;
        this.uebernehmen();
        return;
      } catch {
        // Browser ohne DataTransfer: wie zu gross
      }
    }
    this.datei.value = '';
    this.uebernehmen();
    this.fehlerZeigen(this.dataset.fehlerGroesse);
  }

  spaeterSetzen(an) {
    this.spaeter = an;
    if (an) this.datei.value = '';
    // abgeschaltete Felder gehen nicht mit: entweder die Datei oder „kommt per Mail“
    this.datei.disabled = an;
    if (this.spaeterWert) this.spaeterWert.disabled = !an;
    if (this.spaeterZeile) this.spaeterZeile.hidden = an;
    if (this.spaeterAn) this.spaeterAn.hidden = !an;
    this.classList.toggle('keks-fotofeld--spaeter', an);
    this.fehlerVerstecken();
    this.uebernehmen();
  }

  uebernehmen() {
    if (this.vorschauUrl) {
      URL.revokeObjectURL(this.vorschauUrl);
      this.vorschauUrl = null;
    }
    const datei = this.datei.files && this.datei.files[0];
    if (!datei) {
      this.classList.remove('keks-fotofeld--gewaehlt');
      if (this.gewaehlt) this.gewaehlt.hidden = true;
      return;
    }
    this.fehlerVerstecken();
    if (this.dateiname) this.dateiname.textContent = datei.name;
    if (this.vorschau) {
      if (datei.type && datei.type.indexOf('image/') === 0) {
        this.vorschauUrl = URL.createObjectURL(datei);
        this.vorschau.src = this.vorschauUrl;
        this.vorschau.hidden = false;
      } else {
        this.vorschau.removeAttribute('src');
        this.vorschau.hidden = true;
      }
    }
    if (this.gewaehlt) this.gewaehlt.hidden = false;
    this.classList.add('keks-fotofeld--gewaehlt');
  }

  fehlerZeigen(text) {
    if (!this.fehlerfeld) return;
    this.fehlerfeld.textContent = (typeof text === 'string' && text) || this.dataset.fehlerPflicht || '';
    this.fehlerfeld.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  fehlerVerstecken() {
    if (this.fehlerfeld) this.fehlerfeld.hidden = true;
  }
}
if (!customElements.get('keks-fotofeld')) customElements.define('keks-fotofeld', KeksFotofeld);

function fotofeldOhneBild(formular) {
  return Array.from(document.querySelectorAll('keks-fotofeld[data-pflicht]')).find(
    (feld) => feld instanceof KeksFotofeld && (!formular || feld.formular === formular) && !feld.bereit
  );
}

document.addEventListener(
  'submit',
  (ereignis) => {
    const offen = fotofeldOhneBild(ereignis.target);
    if (!offen) return;
    ereignis.preventDefault();
    ereignis.stopImmediatePropagation();
    offen.fehlerZeigen();
  },
  true
);

document.addEventListener(
  'click',
  (ereignis) => {
    const knopf =
      ereignis.target instanceof Element
        ? ereignis.target.closest(
            'add-to-cart-component button[type="submit"], .sticky-add-to-cart__button, .add-to-cart-button'
          )
        : null;
    if (!knopf) return;
    // Ohne eigenes Formular (Sticky-Leiste) zaehlt jedes Pflichtfeld der Seite.
    const formular = knopf.form || null;
    if (!formular && !knopf.classList.contains('sticky-add-to-cart__button')) return;
    const offen = fotofeldOhneBild(formular);
    if (!offen) return;
    ereignis.preventDefault();
    ereignis.stopImmediatePropagation();
    offen.fehlerZeigen();
  },
  true
);

